/**
 * City Block — walkable street outside the club: RazientSounds club front,
 * 4 themed neighborhoods (16 rentable houses, 250 VC/wk), Grand Mansion (1000 VC/wk),
 * and the owner's permanent 5-story mansion with photo frames.
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { X, Coins, DoorOpen, Sofa, Pencil, ArrowLeft, ArrowRight, ArrowUp, ArrowDown, RefreshCw, LogOut, Crown, Camera, Shield, Trash2 } from 'lucide-react';
import MobileJoystick from './MobileJoystick';
import { drawPlayerSprite, computeTint, resolveGender, ensureSpriteLoaded, directionFromDelta } from './playerSprite';
import { drawSpecialSprite, isSheetSpecial } from './specialSprites';
import { RARITY_META } from './outfitItems';
import { FURNITURE, drawFurniture } from './ApartmentComplex';
import { StreetMusic, zoneForY, zoneLabel } from './streetMusic';
import { Volume2, VolumeX } from 'lucide-react';
import {
  WALK_RECTS, DRIVE_RECTS, MANHOLES, H_NPCS, CAM_MIN_X, CAM_MAX_X, HOUSE_BLOCKS,
  clampToRects, clampPointToRects, drawDistricts,
} from './cityDistricts';
import { SEWER_W, SEWER_H, SEWER_RECTS, LADDERS, drawSewer } from './sewerSystem';
import { fxForItem, fxWobble, drawDrinkEffect } from './drinkEffects';
import { SHOPS, SHOP_BY_NUM, SHOP_FRONTS, SHOP_COUNTER, ARCADE_CABS, VINYL_BOOTH, drawShopScene } from './shopInteriors';
import { WATER, JETSKI_DOCK, AMPH_SCREEN, inWater, drawBeachZone, drawSwimOverlay, drawJetSki, GODS_TOWER, drawGodsTower, drawGodsRoom, drawGodsGate, GODS_GATE } from './beachZone';
import { ROCKET_PAD, MOON_DOORS, SPACE_W, SPACE_H, SPACE_MOON, PLANETS, drawRocketPad, drawMoonRoom, drawSpaceFrames, frameSlotAt, drawSpaceWorld } from './moonZone';
import { PLANET_SPOTS, PLANET_HOUSE_NAMES, drawPlanetRoom } from './planetZone';
import { SkyClubCue } from './SkyClubCue';

const PLANET_KIND_TO_ID = { planet_mars: 'mars', planet_venus: 'venus', planet_saturn: 'saturn' };
const PLANET_HOUSE_NUM = { mars: 971, venus: 972, saturn: 973 };
import { io as ioClient } from 'socket.io-client';
import { DANCE_EMOTES } from './constants';
import { drawChatBubble } from './chatOverlay';

const EMOTE_ICONS = Object.fromEntries(DANCE_EMOTES.map((e) => [e.id, e.icon || e.emoji]));

const W = 800;
const BASE_H = 520;
const WORLD_H = 6360;
const MANSION_NUM = 17;
const ELITE_NUMS = [18, 19];
const ADMIN_NUMS = [20, 21];
const MANSION_TITLES = { 17: 'GRAND MANSION', 18: 'ELITE MANSION I', 19: 'ELITE MANSION II', 20: 'ADMIN MANSION I', 21: 'ADMIN MANSION II' };

// fairgrounds (bottom of the block)
const FERRIS = { x: 170, y: 5090, r: 128 };
const DINER = { x: 640, doorY: 5150 };
// two big tables, 4 seats each — dinner-date ready
const DINER_TABLES = [{ x: 250, y: 355 }, { x: 550, y: 355 }];
const DINER_SEATS = DINER_TABLES.flatMap((tb, ti) => ([
  { x: tb.x, y: tb.y - 46, tx: tb.x, ty: tb.y, ti, wox: 36, woy: -6 },
  { x: tb.x, y: tb.y + 54, tx: tb.x, ty: tb.y, ti, wox: 36, woy: 12 },
  { x: tb.x - 64, y: tb.y + 8, tx: tb.x, ty: tb.y, ti, wox: -32, woy: 30 },
  { x: tb.x + 64, y: tb.y + 8, tx: tb.x, ty: tb.y, ti, wox: 32, woy: 30 },
]));
const DINER_MENU = [
  { id: 'pina_colada', name: 'Piña Colada', icon: '🍹', price: 12, kind: 'drink' },
  { id: 'beach_beer', name: 'Beach Weather Beer', icon: '🍺', price: 8, kind: 'drink' },
  { id: 'blue_apple_beer', name: 'Blue Apple Beer', icon: '🍏', price: 10, kind: 'drink' },
  { id: 'turtlelini', name: 'Turtlelini Martini', icon: '🍸', price: 15, kind: 'drink' },
  { id: 'turtle_burger', name: 'Turtle Shack Burger', icon: '🍔', price: 18, kind: 'food' },
  { id: 'boardwalk_fries', name: 'Boardwalk Fries', icon: '🍟', price: 9, kind: 'food' },
  { id: 'coconut_shrimp', name: 'Coconut Shrimp', icon: '🍤', price: 16, kind: 'food' },
  { id: 'key_lime_pie', name: 'Key Lime Pie', icon: '🥧', price: 12, kind: 'food' },
];

// wall-clock wheel angle — identical on every client, so friends share the same gondola
const ferrisAngle = () => (Date.now() % 3600000) * 0.00025;

const NB = [
  { name: 'NEON ROW', ground: '#141221', body: '#1d1830', roof: '#0e0b1a', door: '#2d2350', accent: '#22d3ee' },
  { name: 'PALM COURT', ground: '#c9b077', body: '#f2e3c4', roof: '#c96f4a', door: '#8a5a30', accent: '#34d399' },
  { name: 'AUTUMN LANE', ground: '#5a4526', body: '#8a4b32', roof: '#4a2c1a', door: '#3a2210', accent: '#fb923c' },
  { name: 'SNOWFALL PINES', ground: '#dfe8f2', body: '#7a5c3e', roof: '#f4f8fc', door: '#4a3520', accent: '#93c5fd' },
];

const SUBS = {
  living: { name: 'Living Room', wall: '#31343f', floor: '#23252e' },
  kitchen: { name: 'Kitchen', wall: '#453a24', floor: '#332b1a' },
  bedroom: { name: 'Bedroom', wall: '#3d2f4f', floor: '#2a2138' },
  bathroom: { name: 'Bathroom', wall: '#1e3a45', floor: '#16303a' },
  m_hall: { name: '🏛️ Grand Hall', wall: '#2a2338', floor: '#1d1830' },
  m_lounge: { name: '🥂 Mansion Lounge', wall: '#332433', floor: '#241a24' },
  m_gallery: { name: '🖼️ Gallery', wall: '#26202e', floor: '#191420' },
  o_lobby: { name: '👑 Mansion Lobby', wall: '#1f1830', floor: '#16101f' },
  o_lounge: { name: '👑 Velvet Lounge', wall: '#2b1d33', floor: '#1e1524' },
  o_game: { name: '👑 Game Room', wall: '#151a2e', floor: '#0f1322' },
  o_gallery: { name: '👑 Royal Gallery', wall: '#241f2e', floor: '#171320' },
  o_roof: { name: '👑 Rooftop Suite', wall: '#0a0e24', floor: '#1a1128' },
  diner: { name: '🐢 Turtle Shack — Beach Bar & Grill', wall: '#1e3a3a', floor: '#c9a877' },
  pizza: { name: '🍕 Pizza Palace', wall: '#3b1a12', floor: '#8a5a34' },
  arcade: { name: '🕹️ Retro Arcade', wall: '#181433', floor: '#221c3f' },
  vinyl: { name: '🎵 Vinyl Vault', wall: '#10222e', floor: '#1a3242' },
  ink: { name: '⚡ Ink & Iron Tattoo', wall: '#1c1220', floor: '#241826' },
  boba: { name: '🧋 Boba Break', wall: '#2e1e2a', floor: '#513c33' },
  g_basement: { name: '🕳️ THEGODS — Basement Vault', wall: '#141019', floor: '#0d0a10' },
  g_lobby: { name: '⚡ THEGODS — Grand Lobby', wall: '#1a1426', floor: '#241a30' },
  g_security: { name: '🔒 THEGODS — Security Room', wall: '#0f1720', floor: '#0a1016' },
  g_bath: { name: '🛁 THEGODS — Marble Bath', wall: '#243441', floor: '#31485a' },
  g_lounge: { name: '👑 THEGODS — Gold Lounge', wall: '#2a2010', floor: '#3a2c14' },
  g_observatory: { name: '🔭 THEGODS — Star Observatory', wall: '#0a0f24', floor: '#101830' },
  g_club: { name: '🪩 THEGODS — Sky Club', wall: '#160a20', floor: '#0e0616' },
  moon_surface: { name: '🌕 The Moon — Lunar Colony', wall: '#05060f', floor: '#9ca3af' },
  sp_basement: { name: '⚛ Space Mansion — Engine Bay', wall: '#0b1120', floor: '#111827' },
  sp_lobby: { name: '✦ Space Mansion — Zero-G Lobby', wall: '#101731', floor: '#141b38' },
  sp_morgan: { name: "♡ Morgan's Room ♡", wall: '#3b1230', floor: '#4a1a3c' },
  sp_gallery: { name: '🖼 Space Mansion — Holo Gallery', wall: '#171226', floor: '#100c1c' },
  sp_lounge: { name: '☄ Space Mansion — Nebula Lounge', wall: '#1c1030', floor: '#150c24' },
  sp_pool: { name: '💧 Space Mansion — Anti-Grav Pool', wall: '#0a1a2e', floor: '#0e2438' },
  sp_observatory: { name: '✦ Space Mansion — Crystal Dome', wall: '#060a1c', floor: '#0a1026' },
  mr_base: { name: "⚡ Randall's Moon Base", wall: '#171022', floor: '#100a18' },
  mars_surface: { name: '🔴 Mars — Red Dunes', wall: '#0c0505', floor: '#9a3412' },
  mars_house: { name: '🔴 Red Dune Hab', wall: '#2d0f0a', floor: '#451a10' },
  mars_secret: { name: '✨ Martian Crystal Cavern', wall: '#180808', floor: '#2a0f14' },
  venus_surface: { name: '🌕 Venus — Cloud Tops', wall: '#241a05', floor: '#a16207' },
  venus_house: { name: '☁️ Cloud Nine Villa', wall: '#3a2a08', floor: '#584010' },
  venus_secret: { name: '✨ Vaporwave Hideout', wall: '#160a24', floor: '#241040' },
  saturn_surface: { name: '🪐 Saturn — Ring Ice', wall: '#040d16', floor: '#a5f3fc' },
  saturn_house: { name: '🪐 Ring View Loft', wall: '#082635', floor: '#0c3346' },
  saturn_secret: { name: '✨ Frozen Treasure Vault', wall: '#02121c', floor: '#08222f' },
};

const CHAINS = {
  house: ['living', 'bedroom'],
  mansion: ['m_hall', 'm_lounge', 'm_gallery'],
  owner_mansion: ['o_lobby', 'o_lounge', 'o_game', 'o_gallery', 'o_roof'],
  diner: ['diner'],
  pizza: ['pizza'],
  arcade: ['arcade'],
  vinyl: ['vinyl'],
  ink: ['ink'],
  boba: ['boba'],
  gods_mansion: ['g_basement', 'g_lobby', 'g_security', 'g_bath', 'g_lounge', 'g_observatory', 'g_club'],
  moon: ['moon_surface'],
  space_mansion: ['sp_basement', 'sp_lobby', 'sp_morgan', 'sp_gallery', 'sp_lounge', 'sp_pool', 'sp_observatory'],
  moon_randall: ['mr_base'],
  planet_mars: ['mars_surface'],
  planet_venus: ['venus_surface'],
  planet_saturn: ['saturn_surface'],
};

const FRAMES = [
  { x: 180, y: 115 }, { x: 400, y: 115 }, { x: 620, y: 115 },
  { x: 180, y: 228 }, { x: 400, y: 228 }, { x: 620, y: 228 },
];

// house 1-16 street position
const housePos = (n) => {
  const nb = Math.floor((n - 1) / 4);
  const idx = (n - 1) % 4;
  const row = Math.floor(idx / 2);
  const side = idx % 2;
  return { x: side ? 675 : 125, y: 400 + nb * 640 + 210 + row * 300, nb, side };
};

// parked cars along the block (side: 0 = left curb x270, 1 = right curb x530)
const CARS = [
  { y: 560, side: 0, color: '#e11d8f', trim: '#22d3ee' },
  { y: 860, side: 1, color: '#22d3ee', trim: '#e11d8f' },
  { y: 1180, side: 1, color: '#fbbf24', trim: '#fff7d6', convertible: true },
  { y: 1450, side: 0, color: '#34d399', trim: '#d1fae5' },
  { y: 1800, side: 0, color: '#b45309', trim: '#fcd34d' },
  { y: 2100, side: 1, color: '#7c2d12', trim: '#fdba74' },
  { y: 2480, side: 1, color: '#475569', trim: '#e2e8f0', snowy: true },
  { y: 2720, side: 0, color: '#1e3a8a', trim: '#bfdbfe', snowy: true },
  { y: 3180, side: 0, color: '#0c0a09', trim: '#FFD700', limo: true },
  { y: 3560, side: 1, color: '#f8fafc', trim: '#FFD700' },
];

// ambient pedestrians strolling the sidewalks — each one a different character
const NPCS = [
  { x: 270, yMin: 480, yMax: 940, speed: 0.55, phase: 0, hue: 0, char: 'punk' },
  { x: 530, yMin: 620, yMax: 1480, speed: 0.42, phase: 400, hue: 0, char: 'surfer' },
  { x: 270, yMin: 1300, yMax: 1990, speed: 0.6, phase: 900, hue: 0, char: 'boxer' },
  { x: 530, yMin: 1900, yMax: 2580, speed: 0.38, phase: 200, hue: 0, char: 'ballerina' },
  { x: 270, yMin: 2450, yMax: 3040, speed: 0.5, phase: 650, hue: 0, char: 'chef' },
  { x: 530, yMin: 2960, yMax: 3520, speed: 0.45, phase: 100, hue: 0, char: 'cowboy' },
  { x: 530, yMin: 480, yMax: 1150, speed: 0.5, phase: 820, hue: 0, char: 'dj' },
  { x: 270, yMin: 3560, yMax: 4180, speed: 0.4, phase: 300, hue: 0, char: 'formal' },
  { x: 530, yMin: 3640, yMax: 4480, speed: 0.48, phase: 750, hue: 0, char: 'royalty' },
  { x: 270, yMin: 4700, yMax: 5400, speed: 0.55, phase: 500, hue: 0, char: 'clown' },
  { x: 530, yMin: 4700, yMax: 5400, speed: 0.42, phase: 950, hue: 0, char: 'wizard' },
];

export default function CityBlock({
  user, avatar, vibecoins, setVibecoins, authFetch, API_URL, socketRef, players = {}, playersLiveRef, currentEmote, emoteEndTime, onExitClub, onGoApartments,
  onPlayerClick, spawnSignal, serverRoomRef, chatMessagesRef,
}) {
  const canvasRef = useRef(null);
  const [H] = useState(() => {
    const vw = window.innerWidth;
    const vh = Math.max(320, window.innerHeight - 64);
    const a = vw / vh;
    if (a >= W / BASE_H) return BASE_H;
    return Math.min(1400, Math.round(W / Math.max(0.5, a)));
  });
  const offY = Math.max(0, Math.round((H - BASE_H) * 0.42));

  const [mode, setMode] = useState('street');
  const [houses, setHouses] = useState([]);
  const [ownerMansion, setOwnerMansion] = useState(null);
  const [interior, setInterior] = useState(null);
  const [ownedItems, setOwnedItems] = useState([]);
  const [rentPrompt, setRentPrompt] = useState(null);
  const [claimPrompt, setClaimPrompt] = useState(false);
  const [adminClaim, setAdminClaim] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [shopMenu, setShopMenu] = useState(null); // downtown shop id ('pizza'|'ink'|'boba')
  const [spacePhotos, setSpacePhotos] = useState({});
  const [guestModal, setGuestModal] = useState(false);
  const [guestList, setGuestList] = useState([]);
  const [guestName, setGuestName] = useState('');
  const [rentPlanet, setRentPlanet] = useState(null); // planet id pending rent confirm
  const spaceHousesRef = useRef({});
  const [drivePrompt, setDrivePrompt] = useState(null);
  const [driving, setDriving] = useState(false);
  const [riding, setRiding] = useState(false);
  const [showShop, setShowShop] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [placingItem, setPlacingItem] = useState(null);
  const [photoRemoveIdx, setPhotoRemoveIdx] = useState(null);
  const [musicOn, setMusicOn] = useState(true);
  const isStaff = ['owner', 'admin', 'overseer'].includes(user?.role);
  const isRandall = (user?.email || '').toLowerCase() === 'randall@razientsounds.com';
  const isMorgan = (user?.email || '').toLowerCase() === 'morgan@razientsounds.com';
  const canMoonHouse = isStaff || isRandall || isMorgan || !!user?.is_vip;

  const posRef = useRef({ x: 520, y: 255 });
  const targetRef = useRef(null);
  const dirRef = useRef('down');
  const walkingRef = useRef(false);
  const walkFrameRef = useRef(0);
  const camRef = useRef(0);
  const phaseRef = useRef(0);
  const seqRef = useRef(0);
  const lastSendRef = useRef(0);
  const lastTouchRef = useRef(0);
  const localPlayersRef = useRef(players);
  useEffect(() => { localPlayersRef.current = players; }, [players]);
  // live ref from Nightclub sees every player_moved packet (state prop only updates on join/leave)
  const playersRef = playersLiveRef || localPlayersRef;
  useEffect(() => {
    myEmoteRef.current = currentEmote ? { id: currentEmote, until: emoteEndTime || 0 } : null;
  }, [currentEmote, emoteEndTime]);
  const modeRef = useRef(mode);
  const interiorRef = useRef(interior);
  const editRef = useRef({ editMode, placingItem });
  const photoImgsRef = useRef(new Map());
  const fileInputRef = useRef(null);
  const musicRef = useRef(null);
  const zoneRef = useRef(null);
  const ferrisRef = useRef(null);   // riding the Ferris wheel
  const dinerRef = useRef(null);    // diner seat + waitress state machine
  const drivingRef = useRef(null);  // {idx, x, y, vx, vy, ang, color, limo} while driving a car
  const jetskiRef = useRef(false);  // riding the Sunset Shore jet-ski
  const volleyRef = useRef({ x: 540, y: 5860, fromX: 540, fromY: 5860, toX: 540, toY: 5860, p: 1, z: 0, count: 0 });
  const liveScreenRef = useRef(null);   // top live room for the amphitheater screen
  const liveCoverImgRef = useRef(null);
  const screenVideoRef = useRef(null);  // hidden <video> fed by the host's WebRTC stream
  const screenPeerRef = useRef(null);
  const screenSockRef = useRef(null);
  const screenRoomRef = useRef(null);
  const gateRef = useRef(0);         // Randall's gate open progress 0..1
  const launchRef = useRef(null);    // {t0} while the rocket lifts off
  const shipAngRef = useRef(-Math.PI / 2); // spacecraft heading in space mode
  const spaceSlotRef = useRef(null); // pending space-mansion frame slot for photo upload
  const spacePhotosRef = useRef({});
  const skyRef = useRef({ allowed: false, can_manage: false }); // THEGODS Sky Club access
  const driveVecRef = useRef({ x: 0, y: 0 });  // joystick vector
  const camXRef = useRef(0);        // horizontal camera (cross streets / districts)
  const carsRef = useRef(CARS.map((c, i) => ({
    idx: i, x: c.side ? 530 : 270, y: c.y + (c.limo ? 54 : 32), angle: -Math.PI / 2, driverSid: null,
  })));
  const passengerRef = useRef(null); // {idx} riding shotgun in someone's car
  const honkRef = useRef(0);
  const fxRef = useRef(null);        // {id, until} active drink effect
  const myEmoteRef = useRef(null);   // {id, until} own emote for canvas bubbles
  const audioCtxRef = useRef(null);
  const knockWaitRef = useRef(null); // pending knock timeout
  const keysRef = useRef({});       // WASD/arrow keys held
  const keyWalkRef = useRef(false);
  const keyDriveRef = useRef(false);

  // Amphitheater mega-screen: poll top live room while in the city
  useEffect(() => {
    let stop = false;
    const teardownFeed = () => {
      try { screenPeerRef.current?.close(); } catch (e) { /* noop */ }
      try { screenSockRef.current?.disconnect(); } catch (e) { /* noop */ }
      screenPeerRef.current = null;
      screenSockRef.current = null;
      screenRoomRef.current = null;
      if (screenVideoRef.current) screenVideoRef.current.srcObject = null;
    };
    // Join the live room as a silent spectator so the big screen shows the REAL camera feed
    const ensureScreenFeed = (top) => {
      if (!top) { teardownFeed(); return; }
      if (screenRoomRef.current === top.room_id) return;
      teardownFeed();
      screenRoomRef.current = top.room_id;
      const s = ioClient(process.env.REACT_APP_BACKEND_URL, { path: '/api/socket.io', transports: ['websocket', 'polling'] });
      screenSockRef.current = s;
      s.on('connect', () => s.emit('live_join', { room_id: top.room_id, name: '📺 City Screen' }));
      s.on('live_signal', async ({ from, data }) => {
        let pc = screenPeerRef.current;
        if (!pc) {
          pc = new RTCPeerConnection({ iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] });
          pc.ontrack = (e) => {
            const v = screenVideoRef.current;
            if (v && v.srcObject !== e.streams[0]) {
              v.srcObject = e.streams[0];
              v.play().catch(() => {});
            }
          };
          pc.onicecandidate = (e) => {
            if (e.candidate) s.emit('live_signal', { room_id: top.room_id, target: from, data: { candidate: e.candidate } });
          };
          screenPeerRef.current = pc;
        }
        try {
          if (data?.sdp) {
            await pc.setRemoteDescription(data.sdp);
            if (data.sdp.type === 'offer') {
              await pc.setLocalDescription();
              s.emit('live_signal', { room_id: top.room_id, target: from, data: { sdp: pc.localDescription } });
            }
          } else if (data?.candidate) {
            await pc.addIceCandidate(data.candidate);
          }
        } catch (e) { /* negotiation hiccup — EQ fallback keeps showing */ }
      });
      s.on('live_ended', teardownFeed);
    };
    const poll = async () => {
      try {
        const res = await fetch(`${API_URL}/api/live/rooms`);
        const data = await res.json();
        if (stop) return;
        const top = (data.rooms || [])[0] || null;
        liveScreenRef.current = top;
        ensureScreenFeed(top);
        if (top?.cover && liveCoverImgRef.current?.src !== top.cover) {
          const im = new Image();
          im.src = top.cover;
          liveCoverImgRef.current = im;
        } else if (!top?.cover) liveCoverImgRef.current = null;
      } catch (e) { /* offline — screen shows idle */ }
    };
    poll();
    const iv = setInterval(poll, 25000);
    return () => { stop = true; clearInterval(iv); teardownFeed(); };
  }, []);

  useEffect(() => {
    musicRef.current = new StreetMusic();
    return () => musicRef.current?.dispose();
  }, []);

  // WASD / arrow keys — desktop movement parity with the club
  useEffect(() => {
    const KEYMAP = { w: 'up', arrowup: 'up', s: 'down', arrowdown: 'down', a: 'left', arrowleft: 'left', d: 'right', arrowright: 'right' };
    const isTyping = (e) => {
      const t = e.target;
      return t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);
    };
    const down = (e) => {
      const key = e.key?.toLowerCase();
      if (key === 'h' && !isTyping(e) && drivingRef.current) { keysRef.current.honkNow = true; return; }
      const k = KEYMAP[key];
      if (!k || isTyping(e)) return;
      if (e.key.startsWith('Arrow')) e.preventDefault();
      keysRef.current[k] = true;
    };
    const up = (e) => {
      const k = KEYMAP[e.key?.toLowerCase()];
      if (k) keysRef.current[k] = false;
    };
    const clear = () => { keysRef.current = {}; };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    window.addEventListener('blur', clear);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
      window.removeEventListener('blur', clear);
    };
  }, []);

  useEffect(() => { localPlayersRef.current = players; }, [players]);
  useEffect(() => { modeRef.current = mode; }, [mode]);
  useEffect(() => { interiorRef.current = interior; }, [interior]);
  useEffect(() => { editRef.current = { editMode, placingItem }; }, [editMode, placingItem]);

  const emitRoom = useCallback((room) => {
    if (serverRoomRef) serverRoomRef.current = room;
    socketRef?.current?.emit('change_room', { room, name: user?.username || user?.name || 'Guest' });
  }, [socketRef, user, serverRoomRef]);

  const loadHouses = useCallback(async () => {
    try {
      const res = await authFetch(`${API_URL}/api/houses`);
      const data = await res?.json();
      if (data?.houses) {
        setHouses(data.houses);
        setOwnerMansion(data.owner_mansion || null);
      }
    } catch (e) { /* noop */ }
  }, [authFetch, API_URL]);

  const loadMine = useCallback(async () => {
    try {
      const res = await authFetch(`${API_URL}/api/houses/my`);
      const data = await res?.json();
      if (data) setOwnedItems(data.owned_items || []);
    } catch (e) { /* noop */ }
  }, [authFetch, API_URL]);

  useEffect(() => { loadHouses(); loadMine(); }, [loadHouses, loadMine]);

  const enterInterior = (house, isOwner) => {
    const chain = CHAINS[house.kind] || CHAINS.house;
    setInterior({
      houseNumber: house.house_number,
      kind: house.kind,
      isOwner,
      subRoom: chain[0],
      furniture: house.furniture || [],
      photos: house.photos || [],
      ownerName: house.owner_username,
      expiresAt: house.expires_at,
    });
    setMode('interior');
    posRef.current = { x: 400, y: 400 };
    targetRef.current = null;
    emitRoom(`house_${house.house_number}_${chain[0]}`);
  };

  const exitToStreet = () => {
    const itr = interiorRef.current;
    setMode('street');
    setInterior(null);
    setEditMode(false);
    setPlacingItem(null);
    dinerRef.current = null;
    setShowMenu(false);
    setShopMenu(null);
    // step back out right at the property's front door
    let spawn = { x: 400, y: 500 };
    const n = itr?.houseNumber;
    if (n >= 1 && n <= 16) {
      const hp = housePos(n);
      spawn = { x: hp.x, y: hp.y + 26 };
    } else if (n === MANSION_NUM) spawn = { x: 140, y: 3362 };
    else if (n === 18) spawn = { x: 140, y: 3992 };
    else if (n === 19) spawn = { x: 660, y: 3992 };
    else if (n === 20) spawn = { x: 140, y: 4432 };
    else if (n === 21) spawn = { x: 660, y: 4432 };
    else if (n === 0) spawn = { x: 660, y: 3512 };
    else if (n === 900) spawn = { x: DINER.x, y: DINER.doorY + 36 };
    else if (n === 950) spawn = { x: 600, y: 5610 };
    else if (n === 955 || n === 960 || n === 961 || (n >= 971 && n <= 973)) spawn = { x: ROCKET_PAD.x + 46, y: ROCKET_PAD.y + 34 };
    else if (SHOP_BY_NUM[n]) {
      const sf = SHOP_FRONTS.find((f) => f.kind === SHOP_BY_NUM[n].kind);
      if (sf) spawn = { x: sf.x + sf.w - 38, y: 700 };
    }
    // safety net — never leave the player stranded outside the walkable map
    spawn = clampPointToRects(spawn.x, spawn.y, WALK_RECTS, HOUSE_BLOCKS);
    posRef.current = spawn;
    targetRef.current = null;
    camRef.current = Math.max(0, Math.min(WORLD_H - H, spawn.y - H * 0.45));
    socketRef?.current?.emit('player_move', {
      x: Math.round(spawn.x), y: Math.round(spawn.y), is_walking: false, direction: 'down',
    });
    emitRoom('city');
    loadHouses();
  };

  const goSub = (sub) => {
    setInterior((prev) => {
      if (!prev) return prev;
      emitRoom(`house_${prev.houseNumber}_${sub}`);
      return { ...prev, subRoom: sub };
    });
    posRef.current = { x: 400, y: 400 };
    targetRef.current = null;
  };

  // visitor: knock accepted for a house
  useEffect(() => {
    const onResult = async (data) => {
      if (data.property !== 'house') return;
      if (knockWaitRef.current) { clearTimeout(knockWaitRef.current); knockWaitRef.current = null; }
      if (data.accepted) {
        toast.success(`${data.owner_name} let you in! 🏡`);
        try {
          const res = await authFetch(`${API_URL}/api/houses/${data.room_number}`);
          const house = await res?.json();
          if (house && house.owner_user_id) enterInterior(house, false);
        } catch (e) { /* noop */ }
      } else {
        toast.info("They didn't let you in this time.");
      }
    };
    // Re-attach whenever the socket instance is replaced (reconnect/recreate)
    let bound = null;
    const attach = () => {
      const s = socketRef?.current;
      if (s && s !== bound) {
        if (bound) bound.off('apartment_knock_result', onResult);
        s.on('apartment_knock_result', onResult);
        bound = s;
      }
    };
    attach();
    const iv = setInterval(attach, 2000);
    return () => { clearInterval(iv); if (bound) bound.off('apartment_knock_result', onResult); };
    // eslint-disable-next-line
  }, []);

  const handleRent = async (houseNumber) => {
    try {
      const res = await authFetch(`${API_URL}/api/houses/rent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ house_number: houseNumber }),
      });
      const data = await res?.json();
      if (res?.ok) {
        setVibecoins(data.new_balance);
        toast.success(data.message);
        setRentPrompt(null);
        loadHouses();
        enterInterior(data.house, true);
      } else toast.error(data?.detail || 'Rent failed');
    } catch (e) { toast.error('Rent failed'); }
  };

  const handleClaim = async () => {
    try {
      const res = await authFetch(`${API_URL}/api/houses/owner-mansion/claim`, { method: 'POST' });
      const data = await res?.json();
      if (res?.ok) {
        toast.success(data.message);
        setClaimPrompt(false);
        loadHouses();
        enterInterior(data.house, true);
      } else toast.error(data?.detail || 'Claim failed');
    } catch (e) { toast.error('Claim failed'); }
  };

  const handleAdminClaim = async () => {
    try {
      const res = await authFetch(`${API_URL}/api/houses/admin-mansion/claim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ house_number: adminClaim }),
      });
      const data = await res?.json();
      if (res?.ok) {
        toast.success(data.message);
        setAdminClaim(null);
        loadHouses();
        if (data.house) enterInterior(data.house, true);
      } else toast.error(data?.detail || 'Claim failed');
    } catch (e) { toast.error('Claim failed'); }
  };

  const handleOrder = async (item) => {
    try {
      const res = await authFetch(`${API_URL}/api/nightclub/bartender/purchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ drink_id: item.id }),
      });
      const data = await res?.json();
      if (res?.ok) {
        if (typeof data.new_balance === 'number') setVibecoins(data.new_balance);
        if (dinerRef.current) {
          dinerRef.current.stage = 'fetching';
          dinerRef.current.t0 = Date.now();
          dinerRef.current.drink = item;
        }
        setShowMenu(false);
        toast.success(`${item.name} coming right up!`);
      } else toast.error(data?.detail || 'Order failed');
    } catch (e) { toast.error('Order failed'); }
  };

  // Downtown shop: order → coins deduct → wearable fx right away
  const handleShopOrder = async (item) => {
    try {
      const res = await authFetch(`${API_URL}/api/nightclub/bartender/purchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ drink_id: item.id }),
      });
      const data = await res?.json();
      if (res?.ok) {
        if (typeof data.new_balance === 'number') setVibecoins(data.new_balance);
        activateDrinkFx(item);
        setShopMenu(null);
        toast.success(`${item.icon} ${item.name} — enjoy!`);
      } else toast.error(data?.detail || 'Order failed');
    } catch (e) { toast.error('Order failed'); }
  };

  // Retro Arcade: 5 VC a play, server rolls the prize
  const arcadeBusyRef = useRef(false);
  const playArcade = async (cabinet) => {
    if (arcadeBusyRef.current) return;
    arcadeBusyRef.current = true;
    try {
      const res = await authFetch(`${API_URL}/api/nightclub/arcade/play`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cabinet }),
      });
      const data = await res?.json();
      if (res?.ok) {
        setVibecoins(data.new_balance);
        if (data.won) toast.success(data.message);
        else toast.info(data.message);
      } else toast.error(data?.detail || 'Cabinet is busy');
    } catch (e) { toast.error('Cabinet is busy'); }
    setTimeout(() => { arcadeBusyRef.current = false; }, 900);
  };

  // Vinyl Vault listening booth — free spin: quick riff + floating notes
  const vinylIdxRef = useRef(0);
  const playVinyl = () => {
    const records = [
      { name: 'Midnight Riddim', notes: [220, 277, 330, 440] },
      { name: 'Deep Dub Sunrise', notes: [196, 247, 294, 392] },
      { name: 'Neon Experimental', notes: [262, 311, 415, 523] },
    ];
    const rec = records[vinylIdxRef.current % records.length];
    vinylIdxRef.current++;
    try {
      if (!audioCtxRef.current) audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const ac = audioCtxRef.current;
      rec.notes.forEach((f, i) => {
        const o = ac.createOscillator();
        const g = ac.createGain();
        o.connect(g); g.connect(ac.destination);
        o.type = 'triangle';
        o.frequency.value = f;
        const t0 = ac.currentTime + i * 0.22;
        g.gain.setValueAtTime(0.001, t0);
        g.gain.exponentialRampToValueAtTime(0.07, t0 + 0.03);
        g.gain.exponentialRampToValueAtTime(0.001, t0 + 0.4);
        o.start(t0); o.stop(t0 + 0.45);
      });
    } catch (e) { /* noop */ }
    fxRef.current = { id: 'notes', until: Date.now() + 2 * 60000 };
    const socket = socketRef?.current;
    if (socket?.connected) socket.emit('avatar_update', { avatar: { ...(avatar || {}), street_fx: fxRef.current } });
    toast.success(`🎧 Now spinning: ${rec.name}`);
  };

  const startDriving = () => {
    const car = CARS[drivePrompt];
    const st = carsRef.current[drivePrompt];
    if (!car || !st) { setDrivePrompt(null); return; }
    drivingRef.current = {
      idx: drivePrompt, x: st.x, y: st.y, vx: 0, vy: 0,
      ang: typeof st.angle === 'number' ? st.angle : -Math.PI / 2,
      color: car.color, limo: car.limo,
    };
    st.driverSid = 'me';
    posRef.current = { x: st.x, y: st.y };
    targetRef.current = null;
    driveVecRef.current = { x: 0, y: 0 };
    ferrisRef.current = null;
    passengerRef.current = null;
    setRiding(false);
    setDriving(true);
    setDrivePrompt(null);
    socketRef?.current?.emit('car_take', { idx: drivePrompt, x: st.x, y: st.y, angle: drivingRef.current.ang });
    toast.success('🚗 Vroom! Joystick or WASD to cruise — press H to honk, PARK to get out');
  };

  const parkCar = () => {
    const drv = drivingRef.current;
    if (!drv) return;
    drivingRef.current = null;
    setDriving(false);
    driveVecRef.current = { x: 0, y: 0 };
    const st = carsRef.current[drv.idx];
    if (st) { st.x = drv.x; st.y = drv.y; st.angle = drv.ang; st.driverSid = null; }
    const out = clampPointToRects(drv.x + 46, drv.y, WALK_RECTS, HOUSE_BLOCKS);
    posRef.current = out;
    socketRef?.current?.emit('car_park', { idx: drv.idx, x: drv.x, y: drv.y, angle: drv.ang });
    socketRef?.current?.emit('player_move', {
      x: Math.round(out.x), y: Math.round(out.y), is_walking: false, direction: 'down', transform: null,
    });
    toast.info('🅿️ Parked! The car stays right here.');
  };

  // ride shotgun in a car someone else is driving
  const hopIn = (idx) => {
    const st = carsRef.current[idx];
    passengerRef.current = { idx, lastSeen: Date.now() };
    ferrisRef.current = null;
    targetRef.current = null;
    if (st) posRef.current = { x: st.x, y: st.y };
    setRiding(true);
    toast.success('🚗 Hopped in! Tap anywhere to hop out');
  };

  const hopOut = (silent = false) => {
    if (!passengerRef.current) return;
    passengerRef.current = null;
    setRiding(false);
    const out = clampPointToRects(posRef.current.x + 46, posRef.current.y, WALK_RECTS, HOUSE_BLOCKS);
    posRef.current = out;
    socketRef?.current?.emit('player_move', {
      x: Math.round(out.x), y: Math.round(out.y), is_walking: false, direction: 'down', transform: null,
    });
    if (!silent) toast.info('Hopped out!');
  };

  const honk = () => {
    if (!drivingRef.current) return;
    honkRef.current = Date.now();
    try {
      if (!audioCtxRef.current) audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const ac = audioCtxRef.current;
      const o = ac.createOscillator();
      const g = ac.createGain();
      o.connect(g); g.connect(ac.destination);
      o.type = 'square';
      o.frequency.value = 330;
      g.gain.setValueAtTime(0.06, ac.currentTime);
      g.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + 0.28);
      o.start();
      o.stop(ac.currentTime + 0.3);
    } catch (e) { /* noop */ }
  };

  // drink/food served at the Turtle Shack → wearable visual effect, synced to others
  const activateDrinkFx = (item) => {
    const f = fxForItem(item?.id);
    if (!f) return;
    fxRef.current = { id: f.fx, until: Date.now() + f.mins * 60000 };
    const socket = socketRef?.current;
    if (socket?.connected) {
      socket.emit('avatar_update', { avatar: { ...(avatar || {}), street_fx: fxRef.current } });
    }
  };

  const handleRenew = async () => {
    const itr = interiorRef.current;
    try {
      if (itr && ADMIN_NUMS.includes(itr.houseNumber)) {
        // admin mansions extend free via the claim endpoint
        const res = await authFetch(`${API_URL}/api/houses/admin-mansion/claim`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ house_number: itr.houseNumber }),
        });
        const data = await res?.json();
        if (res?.ok) {
          setInterior((prev) => prev ? { ...prev, expiresAt: data.expires_at } : prev);
          toast.success(data.message);
        } else toast.error(data?.detail || 'Extend failed');
        return;
      }
      const res = await authFetch(`${API_URL}/api/houses/renew`, { method: 'POST' });
      const data = await res?.json();
      if (res?.ok) {
        setVibecoins(data.new_balance);
        setInterior((prev) => prev ? { ...prev, expiresAt: data.expires_at } : prev);
        toast.success(data.message);
      } else toast.error(data?.detail || 'Renew failed');
    } catch (e) { toast.error('Renew failed'); }
  };

  const handleKnock = async (houseNumber) => {
    try {
      const res = await authFetch(`${API_URL}/api/houses/${houseNumber}/knock`, { method: 'POST' });
      const data = await res?.json();
      if (res?.ok) {
        toast.success(data.message);
        if (knockWaitRef.current) clearTimeout(knockWaitRef.current);
        knockWaitRef.current = setTimeout(() => {
          toast.info('No answer at the door... 🚪 Maybe try again in a bit.');
          knockWaitRef.current = null;
        }, 25000);
      } else toast.error(data?.detail || 'Nobody answered');
    } catch (e) { toast.error('Knock failed'); }
  };

  // debug/testing hook: lets automation read position & camera
  useEffect(() => {
    window.__cityPos = () => ({
      x: posRef.current.x, y: posRef.current.y,
      camX: camXRef.current, cam: camRef.current,
      mode: modeRef.current, driving: !!drivingRef.current,
      kind: interiorRef.current?.kind || null,
      subRoom: interiorRef.current?.subRoom || null,
    });
    window.__cityTeleport = (x, y) => {
      if (typeof x === 'number') posRef.current.x = x;
      if (typeof y === 'number') posRef.current.y = y;
      return { x: posRef.current.x, y: posRef.current.y };
    };
    return () => { delete window.__cityPos; delete window.__cityTeleport; };
  }, []);

  // shared city car state — parked spots + who's driving
  useEffect(() => {
    const onCars = (data) => {
      Object.entries(data?.cars || {}).forEach(([k, c]) => {
        const i = parseInt(k, 10);
        const st = carsRef.current[i];
        if (!st) return;
        if (drivingRef.current?.idx === i) { st.driverSid = c.driver_sid ?? st.driverSid; return; }
        if (typeof c.x === 'number') st.x = c.x;
        if (typeof c.y === 'number') st.y = c.y;
        if (typeof c.angle === 'number') st.angle = c.angle;
        st.driverSid = c.driver_sid ?? null;
      });
    };
    const onDenied = () => {
      toast.error("Someone's already driving that car!");
      if (drivingRef.current) {
        drivingRef.current = null;
        setDriving(false);
      }
    };
    // Re-attach whenever the socket instance is replaced (reconnect/recreate)
    let bound = null;
    const attach = () => {
      const s = socketRef?.current;
      if (s && s !== bound) {
        if (bound) { bound.off('city_cars', onCars); bound.off('car_denied', onDenied); }
        s.on('city_cars', onCars);
        s.on('car_denied', onDenied);
        s.emit('city_cars_get');
        bound = s;
      }
    };
    attach();
    const iv = setInterval(attach, 2000);
    return () => {
      clearInterval(iv);
      if (bound) { bound.off('city_cars', onCars); bound.off('car_denied', onDenied); }
    };
    // eslint-disable-next-line
  }, []);

  // leaving the city while driving/riding — park the car and clear the transform
  useEffect(() => () => {
    const drv = drivingRef.current;
    if (drv) {
      socketRef?.current?.emit('car_park', { idx: drv.idx, x: drv.x, y: drv.y, angle: drv.ang });
      socketRef?.current?.emit('player_move', { x: Math.round(drv.x), y: Math.round(drv.y), is_walking: false, direction: 'down', transform: null });
    } else if (passengerRef.current) {
      socketRef?.current?.emit('player_move', { x: Math.round(posRef.current.x), y: Math.round(posRef.current.y), is_walking: false, direction: 'down', transform: null });
    }
    // eslint-disable-next-line
  }, []);

  const handleBuy = async (itemId) => {
    try {
      const res = await authFetch(`${API_URL}/api/houses/furniture/buy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ item_id: itemId }),
      });
      const data = await res?.json();
      if (res?.ok) {
        setVibecoins(data.new_balance);
        setOwnedItems((prev) => [...prev, itemId]);
        toast.success(data.message);
      } else toast.error(data?.detail || 'Purchase failed');
    } catch (e) { toast.error('Purchase failed'); }
  };

  const saveLayout = async (furniture) => {
    try {
      if (interiorRef.current?.kind === 'space_mansion') {
        await authFetch(`${API_URL}/api/space-mansion/furniture`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ furniture }),
        });
        return;
      }
      await authFetch(`${API_URL}/api/houses/layout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ furniture, owner_mansion: interiorRef.current?.kind === 'owner_mansion', house_number: interiorRef.current?.houseNumber }),
      });
    } catch (e) { /* noop */ }
  };

  const handlePhotoFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const itr = interiorRef.current;
    if (!file || !itr) return;
    toast.info('Framing your photo...');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const up = await authFetch(`${API_URL}/api/upload-image-compressed?quality=55`, { method: 'POST', body: fd });
      const ud = await up?.json();
      if (!up?.ok) { toast.error(ud?.detail || 'Upload failed'); return; }
      if (itr.kind === 'space_mansion' || itr.kind === 'moon_randall') {
        const slot = spaceSlotRef.current;
        spaceSlotRef.current = null;
        const res = await authFetch(`${API_URL}/api/space-mansion/photos`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ slot: String(slot), url: ud.url }),
        });
        const data = await res?.json();
        if (res?.ok) { setSpacePhotos(data.photos || {}); toast.success(data.message); }
        else toast.error(data?.detail || 'Could not frame photo');
        return;
      }
      const res = await authFetch(`${API_URL}/api/houses/photos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: ud.url, owner_mansion: itr.kind === 'owner_mansion', house_number: itr.houseNumber }),
      });
      const data = await res?.json();
      if (res?.ok) {
        setInterior((prev) => prev ? { ...prev, photos: data.photos } : prev);
        toast.success(data.message);
      } else toast.error(data?.detail || 'Could not frame photo');
    } catch (err) { toast.error('Upload failed'); }
  };

  const handleRemovePhoto = async (idx) => {
    const itr = interiorRef.current;
    if (!itr) return;
    if (itr.kind === 'space_mansion' || itr.kind === 'moon_randall') {
      try {
        const res = await authFetch(`${API_URL}/api/space-mansion/photos/${idx}`, { method: 'DELETE' });
        const data = await res?.json();
        if (res?.ok) { setSpacePhotos(data.photos || {}); setPhotoRemoveIdx(null); toast.success(data.message); }
        else toast.error(data?.detail || 'Failed');
      } catch (e) { toast.error('Failed'); }
      return;
    }
    try {
      const res = await authFetch(`${API_URL}/api/houses/photos/remove`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ index: idx, owner_mansion: itr.kind === 'owner_mansion', house_number: itr.houseNumber }),
      });
      const data = await res?.json();
      if (res?.ok) {
        setInterior((prev) => prev ? { ...prev, photos: data.photos } : prev);
        setPhotoRemoveIdx(null);
        toast.success(data.message);
      } else toast.error(data?.detail || 'Failed');
    } catch (e) { toast.error('Failed'); }
  };

  // ==================== INPUT ====================
  const canvasPoint = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const cx = ((e.touches ? e.touches[0].clientX : e.clientX) - rect.left) * (W / rect.width);
    const cy = ((e.touches ? e.touches[0].clientY : e.clientY) - rect.top) * (H / rect.height);
    return { cx, cy };
  };

  // manhole ↔ ladder travel
  const enterSewer = (m) => {
    const ladder = LADDERS.find((l) => l.manhole === m.id) || LADDERS[0];
    setMode('sewer');
    setShowMenu(false);
    dinerRef.current = null;
    posRef.current = { x: ladder.x, y: ladder.y + 26 };
    targetRef.current = null;
    camXRef.current = Math.max(0, Math.min(Math.max(0, SEWER_W - W), ladder.x - W / 2));
    camRef.current = Math.max(0, Math.min(Math.max(0, SEWER_H - H), ladder.y - H / 2));
    emitRoom('sewer');
    socketRef?.current?.emit('player_move', {
      x: Math.round(posRef.current.x), y: Math.round(posRef.current.y), is_walking: false, direction: 'down',
    });
    toast.success('🕳️ You slipped down into the sewers...');
  };

  const exitSewer = (ladder) => {
    const m = MANHOLES.find((mm) => mm.id === ladder.manhole) || MANHOLES[0];
    setMode('street');
    posRef.current = { x: m.x, y: m.y + 24 };
    targetRef.current = null;
    camXRef.current = Math.max(CAM_MIN_X, Math.min(CAM_MAX_X, m.x - 400));
    camRef.current = Math.max(0, Math.min(WORLD_H - H, m.y - H * 0.45));
    emitRoom('city');
    socketRef?.current?.emit('player_move', {
      x: Math.round(m.x), y: Math.round(m.y + 24), is_walking: false, direction: 'down',
    });
    toast.success(`⬆ Climbed out at ${m.label}!`);
  };

  // ==================== MOON / SPACE / THEGODS EXTRAS ====================
  useEffect(() => { spacePhotosRef.current = spacePhotos; }, [spacePhotos]);

  const loadSpacePhotos = async () => {
    try {
      const r = await fetch(`${API_URL}/api/space-mansion`);
      const d = await r.json();
      setSpacePhotos(d.photos || {});
      setInterior((prev) => (prev && prev.kind === 'space_mansion' ? { ...prev, furniture: d.furniture || [] } : prev));
    } catch (e) { /* noop */ }
  };

  const fetchSkyAccess = async () => {
    try {
      const r = await authFetch(`${API_URL}/api/mansion/thegods/access`);
      const d = await r?.json();
      if (r?.ok) { skyRef.current = d; setGuestList(d.guest_list || []); }
    } catch (e) { /* noop */ }
  };

  const addGuest = async () => {
    const name = guestName.trim();
    if (!name) return;
    try {
      const r = await authFetch(`${API_URL}/api/mansion/thegods/guestlist`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name }),
      });
      const d = await r?.json();
      if (r?.ok) { setGuestList(d.guest_list || []); setGuestName(''); toast.success(d.message); }
      else toast.error(d?.detail || 'Failed');
    } catch (e) { toast.error('Failed'); }
  };

  const removeGuest = async (name) => {
    try {
      const r = await authFetch(`${API_URL}/api/mansion/thegods/guestlist/${encodeURIComponent(name)}`, { method: 'DELETE' });
      const d = await r?.json();
      if (r?.ok) setGuestList(d.guest_list || []);
    } catch (e) { /* noop */ }
  };

  const tryGoSub = (sub) => {
    if (sub === 'g_security' && !(isStaff || isRandall)) {
      toast.error("🚫 Randall's private security room — keep out");
      return;
    }
    if (sub === 'g_club' && !(isStaff || isRandall || skyRef.current.allowed)) {
      toast.error('🚫 Sky Club is guest-list only — ask Randall for an invite ☁️');
      return;
    }
    goSub(sub);
  };

  // floor navigation that skips Randall's private security floor for non-staff
  const tryFloor = (dir) => {
    const itr = interiorRef.current;
    if (!itr) return;
    const chain = CHAINS[itr.kind] || [];
    let ni = chain.indexOf(itr.subRoom) + dir;
    if (chain[ni] === 'g_security' && !(isStaff || isRandall)) ni += dir;
    if (ni < 0 || ni >= chain.length) return;
    tryGoSub(chain[ni]);
  };

  const enterMoonSurface = () => {
    drivingRef.current = null;
    jetskiRef.current = false;
    dinerRef.current = null;
    launchRef.current = null;
    setShowMenu(false);
    setShopMenu(null);
    setInterior({ houseNumber: 955, kind: 'moon', isOwner: false, subRoom: 'moon_surface', furniture: [], photos: [], ownerName: 'LUNAR COLONY' });
    setMode('interior');
    posRef.current = { x: 400, y: 430 };
    targetRef.current = null;
    emitRoom('house_955_moon_surface');
  };

  const enterSpaceMansion = () => {
    if (!canMoonHouse) { toast.error('🚫 VIP & owners only — the Space Mansion airlock stays shut'); return; }
    setInterior({ houseNumber: 960, kind: 'space_mansion', isOwner: isStaff, subRoom: 'sp_lobby', furniture: [], photos: [], ownerName: 'THE OWNER · SPACE MANSION' });
    posRef.current = { x: 400, y: 420 };
    targetRef.current = null;
    emitRoom('house_960_sp_lobby');
    loadSpacePhotos();
    toast.success('✦ Welcome to the Space Mansion — basement + 6 floors');
  };

  const enterRandallBase = () => {
    if (!canMoonHouse) { toast.error('🚫 VIP & owners only'); return; }
    setInterior({ houseNumber: 961, kind: 'moon_randall', isOwner: false, subRoom: 'mr_base', furniture: [], photos: [], ownerName: 'RANDALL · MOON BASE' });
    posRef.current = { x: 400, y: 420 };
    targetRef.current = null;
    emitRoom('house_961_mr_base');
    loadSpacePhotos();
    toast.success('⚡ Welcome to the Moon Base');
  };

  const enterSpaceFlight = (nearPlanetId) => {
    setInterior(null);
    setMode('space');
    const p = nearPlanetId ? PLANETS.find((pl) => pl.id === nearPlanetId) : null;
    posRef.current = p
      ? { x: p.x, y: p.y - p.r - 110 }
      : { x: SPACE_MOON.x, y: SPACE_MOON.y - SPACE_MOON.r - 110 };
    targetRef.current = null;
    shipAngRef.current = -Math.PI / 2;
    camXRef.current = Math.max(0, Math.min(SPACE_W - W, posRef.current.x - W / 2));
    camRef.current = Math.max(0, Math.min(SPACE_H - H, posRef.current.y - H / 2));
    emitRoom('space');
    loadSpaceHouses();
    socketRef?.current?.emit('player_move', {
      x: Math.round(posRef.current.x), y: Math.round(posRef.current.y), is_walking: false, direction: 'up',
    });
    toast.success('🛸 Cruising the cosmos — planets & the Moon are landing zones');
  };

  const loadSpaceHouses = async () => {
    try {
      const r = await fetch(`${API_URL}/api/space-houses`);
      const d = await r.json();
      const map = {};
      (d.houses || []).forEach((h) => { map[h.planet] = h; });
      spaceHousesRef.current = map;
    } catch (e) { /* noop */ }
  };

  const enterPlanet = (pid) => {
    const name = { mars: 'Mars', venus: 'Venus', saturn: 'Saturn' }[pid];
    setInterior({
      houseNumber: PLANET_HOUSE_NUM[pid], kind: `planet_${pid}`, isOwner: false,
      subRoom: `${pid}_surface`, furniture: [], photos: [], ownerName: name.toUpperCase(),
    });
    setMode('interior');
    posRef.current = { x: 400, y: 430 };
    targetRef.current = null;
    emitRoom(`house_${PLANET_HOUSE_NUM[pid]}_${pid}_surface`);
    loadSpaceHouses();
    toast.success(`🪐 Touchdown on ${name}!`);
  };

  const confirmRentPlanet = async () => {
    const pid = rentPlanet;
    if (!pid) return;
    try {
      const r = await authFetch(`${API_URL}/api/space-houses/${pid}/rent`, { method: 'POST' });
      const d = await r?.json();
      if (r?.ok) {
        toast.success(d.message);
        setVibecoins(d.new_balance);
        setRentPlanet(null);
        await loadSpaceHouses();
        const itr = interiorRef.current;
        if (itr && PLANET_KIND_TO_ID[itr.kind] === pid) {
          setInterior({ ...itr, subRoom: `${pid}_house` });
          posRef.current = { x: 400, y: 420 }; targetRef.current = null;
          emitRoom(`house_${itr.houseNumber}_${pid}_house`);
        }
      } else {
        toast.error(d?.detail || 'Could not rent');
        setRentPlanet(null);
      }
    } catch (e) { toast.error('Could not rent'); setRentPlanet(null); }
  };

  // Admin teleport landing: jump straight to street/sewer coords
  useEffect(() => {
    if (!spawnSignal?.ts) return;
    const { room, x, y } = spawnSignal;
    drivingRef.current = null;
    passengerRef.current = null;
    ferrisRef.current = null;
    dinerRef.current = null;
    targetRef.current = null;
    setInterior(null);
    if (room === 'sewer') {
      setMode('sewer');
      posRef.current = { x, y };
      camXRef.current = Math.max(0, Math.min(Math.max(0, SEWER_W - W), x - W / 2));
      camRef.current = Math.max(0, Math.min(Math.max(0, SEWER_H - H), y - H / 2));
      emitRoom('sewer');
    } else {
      setMode('street');
      posRef.current = { x, y };
      camXRef.current = Math.max(CAM_MIN_X, Math.min(CAM_MAX_X, x - 400));
      camRef.current = Math.max(0, Math.min(WORLD_H - H, y - H * 0.45));
      emitRoom('city');
    }
    socketRef?.current?.emit('player_move', {
      x: Math.round(x), y: Math.round(y), is_walking: false, direction: 'down',
    });
  }, [spawnSignal?.ts]);

  const handleClick = (e) => {
    musicRef.current?.ensureStarted();
    const { cx, cy } = canvasPoint(e);
    if (modeRef.current === 'sewer') {
      const wx = cx + camXRef.current;
      const wy = cy + camRef.current;
      for (const l of LADDERS) {
        if (Math.abs(wx - l.x) < 28 && Math.abs(wy - (l.y - 10)) < 46) { exitSewer(l); return; }
      }
      // tap a player down in the sewers — open their profile
      if (onPlayerClick) {
        for (const [psid, p] of Object.entries(playersRef.current || {})) {
          if ((p.room || 'main') !== 'sewer' || p.x == null) continue;
          if (Math.abs(wx - p.x) < 30 && Math.abs(wy - p.y) < 46) { onPlayerClick(psid, p); return; }
        }
      }
      targetRef.current = clampPointToRects(wx, wy, SEWER_RECTS);
      return;
    }
    if (modeRef.current === 'space') {
      const wx = cx + camXRef.current;
      const wy = cy + camRef.current;
      targetRef.current = {
        x: Math.max(30, Math.min(SPACE_W - 30, wx)),
        y: Math.max(30, Math.min(SPACE_H - 30, wy)),
      };
      return;
    }
    if (modeRef.current === 'street') {
      if (launchRef.current) return; // mid-liftoff
      const wx = cx + camXRef.current;
      const wy = cy + camRef.current;
      // riding shotgun: any tap hops out
      if (passengerRef.current) { hopOut(); return; }
      // driving: tap your car to park; other taps ignored while driving
      if (drivingRef.current) {
        const drv = drivingRef.current;
        if (Math.abs(wx - drv.x) < 48 && Math.abs(wy - drv.y) < 72) parkCar();
        return;
      }
      // riding the Ferris wheel — any tap hops off
      if (ferrisRef.current) {
        ferrisRef.current = null;
        posRef.current = { x: FERRIS.x + 66, y: FERRIS.y + 150 };
        targetRef.current = null;
        socketRef?.current?.emit('player_move', {
          x: Math.round(posRef.current.x), y: Math.round(posRef.current.y), is_walking: false, direction: 'down',
        });
        toast.info('Hopped off the Ferris wheel!');
        return;
      }
      // tap a player — open their profile card
      if (onPlayerClick) {
        for (const [psid, p] of Object.entries(playersRef.current || {})) {
          if ((p.room || 'main') !== 'city' || p.x == null) continue;
          const tf = p.transform;
          if (tf && (typeof tf.car === 'number' || typeof tf.in_car === 'number')) continue;
          if (Math.abs(wx - p.x) < 30 && Math.abs(wy - p.y) < 46) { onPlayerClick(psid, p); return; }
        }
      }
      // club front doors
      if (wy < 290 && wx > 330 && wx < 470) { onExitClub?.(); return; }
      // apartment tower door
      if (wy < 290 && wx > 615 && wx < 800) { onGoApartments?.(); return; }
      // manholes — the way down
      for (const m of MANHOLES) {
        if (Math.abs(wx - m.x) < 26 && Math.abs(wy - m.y) < 20) { enterSewer(m); return; }
      }
      // cars — tap to drive (or hop in if someone's behind the wheel)
      for (let ci = 0; ci < carsRef.current.length; ci++) {
        const st = carsRef.current[ci];
        if (Math.abs(wx - st.x) < 40 && Math.abs(wy - st.y) < 62) {
          if (st.driverSid && st.driverSid !== 'me') hopIn(ci);
          else setDrivePrompt(ci);
          return;
        }
      }
      // houses 1-16
      for (let n = 1; n <= 16; n++) {
        const hp = housePos(n);
        if (Math.abs(wx - hp.x) < 75 && Math.abs(wy - (hp.y - 60)) < 85) {
          const info = houses.find((h) => h.house_number === n);
          if (info?.occupied && info.owner_user_id === user?.user_id) {
            authFetch(`${API_URL}/api/houses/${n}`).then((r) => r?.json()).then((h) => {
              if (h && h.owner_user_id) enterInterior(h, true);
            });
          } else if (info?.occupied) {
            handleKnock(n);
          } else {
            setRentPrompt(n);
          }
          return;
        }
      }
      // big mansions (grand/elite/admin): live check → enter / knock / rent / claim
      const openMansion = (n) => {
        authFetch(`${API_URL}/api/houses/${n}`).then((r) => (r?.ok ? r.json() : null)).then((h) => {
          if (h && h.owner_user_id === user?.user_id) enterInterior(h, true);
          else if (h && h.owner_user_id) handleKnock(n);
          else if (ADMIN_NUMS.includes(n)) {
            if (isStaff) setAdminClaim(n);
            else toast.info('Admin mansions are staff-only 🛡️');
          } else setRentPrompt(n);
        }).catch(() => {});
      };
      // grand mansion (left, millionaire row)
      if (wx < 280 && wx > -100 && wy > 3130 && wy < 3450) { openMansion(MANSION_NUM); return; }
      // elite mansions (18 left / 19 right)
      if (wy > 3770 && wy < 4090) {
        if (wx < 280 && wx > -100) { openMansion(18); return; }
        if (wx > 520 && wx < 900) { openMansion(19); return; }
      }
      // admin mansions (20 left / 21 right)
      if (wy > 4210 && wy < 4530) {
        if (wx < 280 && wx > -100) { openMansion(20); return; }
        if (wx > 520 && wx < 900) { openMansion(21); return; }
      }
      // Ferris wheel — hop on (shared gondola: sit next to a friend already riding)
      if (Math.abs(wx - FERRIS.x) < 150 && Math.abs(wy - FERRIS.y) < 165) {
        const a0 = ferrisAngle();
        const g0 = { x: FERRIS.x + Math.cos(a0) * FERRIS.r, y: FERRIS.y + Math.sin(a0) * FERRIS.r + 24 };
        const occupied = Object.values(playersRef.current).some(
          (p) => (p.room || 'main') === 'city' && Math.abs((p.x ?? 0) - g0.x) < 48 && Math.abs((p.y ?? 0) - g0.y) < 48
        );
        ferrisRef.current = { startedAt: Date.now(), seat: occupied ? 13 : -13 };
        targetRef.current = null;
        walkingRef.current = false;
        toast.success(occupied ? '🎡 Hopped in next to your friend! Tap anywhere to hop off' : '🎡 Enjoy the ride! Tap anywhere to hop off');
        return;
      }
      // Turtle Shack diner door
      if (wx > 500 && wx < 800 && wy > DINER.doorY - 170 && wy < DINER.doorY + 44) {
        dinerRef.current = null;
        setInterior({
          houseNumber: 900, kind: 'diner', isOwner: false, subRoom: 'diner',
          furniture: [], photos: [], ownerName: 'Shelly the Waitress',
        });
        setMode('interior');
        posRef.current = { x: 400, y: 420 };
        targetRef.current = null;
        emitRoom('house_900_diner');
        return;
      }
      // ===== THEGODS clifftop tower (Randall's mansion) =====
      if (wx > GODS_TOWER.x1 && wx < GODS_TOWER.x2 && wy > GODS_TOWER.y1 && wy < GODS_TOWER.y2) {
        dinerRef.current = null;
        setInterior({
          houseNumber: 950, kind: 'gods_mansion', isOwner: false, subRoom: 'g_lobby',
          furniture: [], photos: [], ownerName: 'THEGODS · RANDALL',
        });
        setMode('interior');
        posRef.current = { x: 400, y: 420 };
        targetRef.current = null;
        emitRoom('house_950_g_lobby');
        fetchSkyAccess();
        toast.success('⚡ Welcome to THEGODS');
        return;
      }
      // ===== Sunset Shore =====
      if (wy > 5540) {
        // rocket pad → fly to the Moon
        if (Math.abs(wx - ROCKET_PAD.x) < 70 && wy > ROCKET_PAD.y - 100 && wy < ROCKET_PAD.y + 60) {
          launchRef.current = { t0: Date.now() };
          targetRef.current = null;
          walkingRef.current = false;
          toast.success('🚀 3…2…1… LIFTOFF!');
          return;
        }
        // amphitheater screen tap → watch the live stream
        if (wx > AMPH_SCREEN.x1 && wx < AMPH_SCREEN.x2 && wy > AMPH_SCREEN.y1 && wy < AMPH_SCREEN.y2) {
          const lr = liveScreenRef.current;
          window.location.href = lr?.room_id ? `/live/${lr.room_id}` : '/live';
          return;
        }
        // jet-ski dock
        if (!jetskiRef.current && Math.abs(wx - JETSKI_DOCK.x) < 60 && Math.abs(wy - JETSKI_DOCK.y) < 60) {
          jetskiRef.current = true;
          posRef.current = { x: JETSKI_DOCK.x + 30, y: WATER.y1 + 50 };
          targetRef.current = null;
          toast.success('🌊 Jet-ski! Cruise the water — ride ashore to park');
          return;
        }
        // volleyball bump
        const v = volleyRef.current;
        if (v.p >= 1 && Math.abs(wx - v.x) < 45 && Math.abs(wy - v.y) < 45) {
          v.fromX = v.x; v.fromY = v.y;
          v.toX = v.x > 605 ? 500 + Math.random() * 85 : 625 + Math.random() * 90;
          v.toY = 5790 + Math.random() * 150;
          v.p = 0; v.count++;
          if (v.count % 5 === 0) toast.success(`🏐 Rally x${v.count}! Keep it up!`);
          return;
        }
      }
      // Downtown Strip shops — walk right in (zone includes awning + sign for easy taps)
      if (wy > 468 && wy < 672) {
        for (const sf of SHOP_FRONTS) {
          if (wx > sf.x && wx < sf.x + sf.w) {
            const shop = SHOPS[sf.kind];
            dinerRef.current = null;
            setInterior({
              houseNumber: shop.num, kind: shop.kind, isOwner: false, subRoom: shop.kind,
              furniture: [], photos: [], ownerName: shop.npcTitle,
            });
            setMode('interior');
            posRef.current = { x: 400, y: 420 };
            targetRef.current = null;
            emitRoom(`house_${shop.num}_${shop.kind}`);
            return;
          }
        }
      }
      // owner's 5-story mansion (right, end of block) — live check
      if (wx > 520 && wx < 900 && wy > 3100 && wy < 3560) {
        authFetch(`${API_URL}/api/houses/0`).then((r) => (r?.ok ? r.json() : null)).then((h) => {
          if (h && h.owner_user_id === user?.user_id) enterInterior(h, true);
          else if (h && h.owner_user_id) handleKnock(0);
          else if (isStaff) setClaimPrompt(true);
          else toast.info('This mansion is reserved for the club owner 👑');
        }).catch(() => {});
        return;
      }
      targetRef.current = clampPointToRects(wx, Math.max(300, Math.min(WORLD_H - 30, wy)), WALK_RECTS, HOUSE_BLOCKS);
    } else {
      const itr = interiorRef.current;
      const cy2 = cy - offY;
      const { editMode: em, placingItem: pi } = editRef.current;
      if (itr && em && itr.isOwner) {
        const placed = (itr.furniture || []).filter((f) => f.sub_room === itr.subRoom);
        for (let i = placed.length - 1; i >= 0; i--) {
          const f = placed[i];
          if (Math.abs(cx - f.x) < 40 && Math.abs(cy2 - f.y) < 45) {
            const next = itr.furniture.filter((ff) => ff !== f);
            setInterior({ ...itr, furniture: next });
            saveLayout(next);
            toast.info('Item returned to inventory');
            return;
          }
        }
        if (pi) {
          const next = [...(itr.furniture || []), {
            item_id: pi,
            sub_room: itr.subRoom,
            x: Math.round(Math.max(60, Math.min(740, cx))),
            y: Math.round(Math.max(160, Math.min(470, cy2))),
          }];
          setInterior({ ...itr, furniture: next });
          setPlacingItem(null);
          saveLayout(next);
          return;
        }
        return;
      }
      if (!itr) return;
      // tap a player inside — open their profile card
      if (onPlayerClick && itr.houseNumber != null) {
        const roomIdItr = `house_${itr.houseNumber}_${itr.subRoom}`;
        for (const [psid, p] of Object.entries(playersRef.current || {})) {
          if ((p.room || 'main') !== roomIdItr || p.x == null) continue;
          if (Math.abs(cx - p.x) < 30 && Math.abs(cy2 - p.y) < 46) { onPlayerClick(psid, p); return; }
        }
      }
      // gallery photo frames (owner: add/remove)
      if ((itr.subRoom === 'm_gallery' || itr.subRoom === 'o_gallery') && itr.isOwner) {
        for (let i = 0; i < FRAMES.length; i++) {
          const f = FRAMES[i];
          if (Math.abs(cx - f.x) < 66 && Math.abs(cy2 - f.y) < 50) {
            if ((itr.photos || [])[i]) setPhotoRemoveIdx(i);
            else fileInputRef.current?.click();
            return;
          }
        }
      }
      // Space Mansion & Moon Base shaped photo frames — checked BEFORE the stairs
      // zone (right-side frames sit inside it and were stealing the tap)
      if ((itr.kind === 'space_mansion' && isStaff) || (itr.kind === 'moon_randall' && (isStaff || isRandall))) {
        const slot = frameSlotAt(itr.subRoom, cx, cy2);
        if (slot >= 0) {
          if (spacePhotosRef.current[String(slot)]) setPhotoRemoveIdx(slot);
          else { spaceSlotRef.current = slot; fileInputRef.current?.click(); }
          return;
        }
      }
      // stairs
      if (itr.kind === 'house') {
        if (itr.subRoom === 'living' && cx < 180 && cy2 > 130 && cy2 < 335) { goSub('bedroom'); return; }
        if (itr.subRoom === 'bedroom' && cx > 630 && cy2 > 140 && cy2 < 330) { goSub('living'); return; }
        if (itr.subRoom === 'bedroom' && cx > 52 && cx < 128 && cy2 > 160 && cy2 < 300) { goSub('bathroom'); return; }
        if (itr.subRoom === 'bathroom' && cx > 52 && cx < 128 && cy2 > 160 && cy2 < 300) { goSub('bedroom'); return; }
      } else {
        const chain = CHAINS[itr.kind];
        const ci = chain.indexOf(itr.subRoom);
        if (chain.length > 1 && ci >= 0 && cx > 630 && cx < 785 && cy2 > 140 && cy2 < 330) {
          tryFloor(ci < chain.length - 1 ? 1 : -1);
          return;
        }
      }
      // ===== Moon surface interactions =====
      if (itr.subRoom === 'moon_surface') {
        // Space Mansion airlock
        if (Math.abs(cx - 265) < 62 && cy2 > 288 && cy2 < 394) { enterSpaceMansion(); return; }
        // Randall's Moon Base door
        if (Math.abs(cx - 640) < 72 && cy2 > 262 && cy2 < 352) { enterRandallBase(); return; }
        // rocket → fly home to the beach
        if (Math.abs(cx - MOON_DOORS.rocket.x) < 58 && Math.abs(cy2 - (MOON_DOORS.rocket.y - 20)) < 78) {
          toast.success('🚀 Heading home to Sunset Shore!');
          exitToStreet();
          return;
        }
        // mini spacecraft → free-fly space
        if (Math.abs(cx - MOON_DOORS.craft.x) < 58 && Math.abs(cy2 - MOON_DOORS.craft.y) < 62) {
          enterSpaceFlight();
          return;
        }
      }
      // ===== Planet surface interactions =====
      if (PLANET_KIND_TO_ID[itr.kind]) {
        const pid = PLANET_KIND_TO_ID[itr.kind];
        if (itr.subRoom === `${pid}_surface`) {
          // ship → back to space
          if (Math.abs(cx - PLANET_SPOTS.ship.x) < 56 && Math.abs(cy2 - PLANET_SPOTS.ship.y) < 60) {
            enterSpaceFlight(pid);
            return;
          }
          // house door → enter if it's yours, rent if free
          if (Math.abs(cx - PLANET_SPOTS.house.x) < 70 && cy2 > PLANET_SPOTS.house.y - 30 && cy2 < PLANET_SPOTS.house.y + 90) {
            const rental = spaceHousesRef.current[pid];
            if (rental?.renter_user_id && rental.renter_user_id === user?.user_id) {
              setInterior({ ...itr, subRoom: `${pid}_house` });
              posRef.current = { x: 400, y: 420 }; targetRef.current = null;
              emitRoom(`house_${itr.houseNumber}_${pid}_house`);
            } else if (rental?.renter_user_id && !isStaff) {
              toast.error(`🔒 Rented by ${rental.renter_name} — come back when the lease is up`);
            } else if (rental?.renter_user_id && isStaff) {
              setInterior({ ...itr, subRoom: `${pid}_house` });
              posRef.current = { x: 400, y: 420 }; targetRef.current = null;
              emitRoom(`house_${itr.houseNumber}_${pid}_house`);
            } else {
              setRentPlanet(pid);
            }
            return;
          }
          // secret entrance 🤫
          const sec = PLANET_SPOTS.secret[pid];
          if (sec && Math.abs(cx - sec.x) < 46 && Math.abs(cy2 - sec.y) < 46) {
            setInterior({ ...itr, subRoom: `${pid}_secret` });
            posRef.current = { x: 400, y: 430 }; targetRef.current = null;
            emitRoom(`house_${itr.houseNumber}_${pid}_secret`);
            toast.success('🤫 You found a secret!');
            return;
          }
        } else {
          // inside house/secret — exit mat back to the surface
          if (cx > 325 && cx < 475 && cy2 > 440) {
            setInterior({ ...itr, subRoom: `${pid}_surface` });
            posRef.current = { x: 400, y: 430 }; targetRef.current = null;
            emitRoom(`house_${itr.houseNumber}_${pid}_surface`);
            return;
          }
        }
      }
      // Downtown shop interactions
      if (SHOPS[itr.kind]) {
        const shop = SHOPS[itr.kind];
        if (shop.menu.length && cx > SHOP_COUNTER.x && cx < SHOP_COUNTER.x + SHOP_COUNTER.w &&
            cy2 > SHOP_COUNTER.y && cy2 < SHOP_COUNTER.y + SHOP_COUNTER.h) {
          setShopMenu(itr.kind);
          return;
        }
        if (itr.kind === 'arcade') {
          for (let i = 0; i < ARCADE_CABS.length; i++) {
            const cab = ARCADE_CABS[i];
            if (Math.abs(cx - cab.x) < 40 && cy2 > cab.y - 140 && cy2 < cab.y + 20) {
              playArcade(['SPACE RAID', 'TURBO GP', 'PIXL PUNCH'][i]);
              return;
            }
          }
        }
        if (itr.kind === 'vinyl' && cx > VINYL_BOOTH.x - 10 && cx < VINYL_BOOTH.x + VINYL_BOOTH.w + 10 &&
            cy2 > VINYL_BOOTH.y - 100 && cy2 < VINYL_BOOTH.y + VINYL_BOOTH.h) {
          playVinyl();
          return;
        }
      }
      // Turtle Shack: tap a seat to sit; tap the floor to stand back up
      if (itr.kind === 'diner') {
        for (let i = 0; i < DINER_SEATS.length; i++) {
          const s2 = DINER_SEATS[i];
          if (Math.abs(cx - s2.x) < 26 && Math.abs(cy2 - s2.y) < 30) {
            if (dinerRef.current && dinerRef.current.seat === i) return;
            const taken = Object.values(playersRef.current || {}).some(
              (p) => (p.room || 'main') === 'house_900_diner' &&
                Math.abs((p.x ?? 0) - s2.x) < 20 && Math.abs((p.y ?? 0) - s2.y) < 24
            );
            if (taken) { toast.info("That seat's taken — grab one next to them! 🪑"); return; }
            dinerRef.current = { seat: i, stage: 'coming', t0: Date.now(), drink: null };
            posRef.current = { x: s2.x, y: s2.y };
            targetRef.current = null;
            walkingRef.current = false;
            socketRef?.current?.emit('player_move', { x: s2.x, y: s2.y, is_walking: false, direction: 'down' });
            return;
          }
        }
        if (dinerRef.current) {
          dinerRef.current = null;
          setShowMenu(false);
        }
      }
      // exit door mat (entry room, bottom center) — walk out the front door
      if (((itr.subRoom === CHAINS[itr.kind][0] && itr.kind !== 'moon') || itr.subRoom === 'g_lobby' || itr.subRoom === 'sp_lobby') && cx > 325 && cx < 475 && cy2 > 440) {
        targetRef.current = { x: 400, y: 472 };
        return;
      }
      targetRef.current = {
        x: Math.max(70, Math.min(730, cx)),
        y: Math.max(300, Math.min(480, cy2)),
      };
    }
  };

  // ==================== RENDER LOOP ====================
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let raf;

    const chatFor = (pid) => {
      const msgs = chatMessagesRef?.current || [];
      for (let i = msgs.length - 1; i >= 0; i--) {
        const ttl = Math.min(9000, 3000 + (msgs[i].text?.length || 0) * 60);
        if (msgs[i].playerId === pid && Date.now() - msgs[i].timestamp < ttl) return msgs[i];
      }
      return null;
    };

    // smooth remote players — lerp toward last network position instead of snapping
    const lerpMap = {};
    const lerpPos = (sid, tx, ty) => {
      let e = lerpMap[sid];
      if (!e || Math.abs(e.x - tx) > 240 || Math.abs(e.y - ty) > 240) { e = { x: tx, y: ty }; lerpMap[sid] = e; }
      e.x += (tx - e.x) * 0.18;
      e.y += (ty - e.y) * 0.18;
      return e;
    };

    const drawMyAvatar = (x, y) => {
      const walking = walkingRef.current;
      if (walking) walkFrameRef.current += 0.28;
      const wp = walkFrameRef.current;
      const fx = fxRef.current && fxRef.current.until > Date.now() ? fxRef.current.id : null;
      const xw = x + (walking && fx ? fxWobble(fx, phaseRef.current) : 0);
      const inSea = modeRef.current === 'street' && inWater(posRef.current.x, posRef.current.y);
      const onSki = inSea && jetskiRef.current;
      if (onSki) drawJetSki(ctx, xw, y, dirRef.current, phaseRef.current, walking);
      const swimming = inSea && !onSki;
      if (swimming) { ctx.save(); ctx.beginPath(); ctx.rect(xw - 44, y - 96, 88, 70); ctx.clip(); }
      if (isSheetSpecial(avatar?.special)) {
        drawSpecialSprite(ctx, avatar.special, xw, y - 30, 1, dirRef.current, walking, wp);
      } else {
        const g = resolveGender(avatar?.gender);
        drawPlayerSprite(ctx, xw, y, dirRef.current, walking, wp, 1.4, computeTint(avatar), g);
      }
      if (swimming) { ctx.restore(); drawSwimOverlay(ctx, xw, y, phaseRef.current); }
      if (fx) drawDrinkEffect(ctx, fx, xw, y, phaseRef.current);
      const em = myEmoteRef.current;
      const emActive = em?.id && em.until > Date.now();
      if (emActive) drawEmoteBubble(xw, y, em.id);
      const myChat = chatFor(user?.user_id || 'me');
      if (myChat && !emActive) drawChatBubble(ctx, xw, y - 84, myChat.text, myChat.role, false);
      ctx.font = 'bold 11px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillStyle = '#fff';
      ctx.shadowColor = '#000';
      ctx.shadowBlur = 4;
      ctx.fillText(user?.username || user?.name || 'You', xw, y + 14);
      ctx.shadowBlur = 0;
    };

    const drawRemotePlayer = (p, x, y, sid) => {
      const a = p.avatar || {};
      const dir = ['up', 'down', 'left', 'right'].includes(p.direction) ? p.direction : 'down';
      const rfx = a.street_fx && a.street_fx.until > Date.now() ? a.street_fx.id : null;
      const xw = x + (p.is_walking && rfx ? fxWobble(rfx, phaseRef.current) : 0);
      const rSwim = modeRef.current === 'street' && inWater(p.x, p.y);
      if (rSwim && p.is_walking) drawJetSki(ctx, xw, y, dir, phaseRef.current, true);
      const rClip = rSwim && !p.is_walking;
      if (rClip) { ctx.save(); ctx.beginPath(); ctx.rect(xw - 44, y - 96, 88, 70); ctx.clip(); }
      if (isSheetSpecial(a.special)) {
        drawSpecialSprite(ctx, a.special, xw, y - 30, 1, dir, p.is_walking, phaseRef.current * 0.2);
      } else {
        const g = resolveGender(a.gender);
        ensureSpriteLoaded(g);
        drawPlayerSprite(ctx, xw, y, dir, p.is_walking, phaseRef.current * 0.2, 1.4, computeTint(a), g);
      }
      if (rClip) { ctx.restore(); drawSwimOverlay(ctx, xw, y, phaseRef.current); }
      if (rfx) drawDrinkEffect(ctx, rfx, xw, y, phaseRef.current);
      const remoteEmote = p.currentEmote && (p.emoteEndTime || 0) > Date.now();
      if (remoteEmote) drawEmoteBubble(xw, y, p.currentEmote);
      const pChat = sid ? chatFor(sid) : null;
      if (pChat && !remoteEmote) drawChatBubble(ctx, xw, y - 84, pChat.text, pChat.role, false);
      ctx.font = '10px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillStyle = 'rgba(255,255,255,0.8)';
      ctx.fillText(p.name || 'Guest', xw, y + 13);
    };

    const drawEmoteBubble = (x, y, emoteId) => {
      const icon = EMOTE_ICONS[emoteId] || '✨';
      const bob2 = Math.sin(phaseRef.current * 0.15) * 3;
      ctx.save();
      ctx.fillStyle = 'rgba(255,255,255,0.94)';
      ctx.beginPath();
      ctx.arc(x, y - 80 + bob2, 15, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.moveTo(x - 5, y - 68 + bob2);
      ctx.lineTo(x + 5, y - 68 + bob2);
      ctx.lineTo(x, y - 59 + bob2);
      ctx.closePath();
      ctx.fill();
      ctx.font = '17px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(icon, x, y - 74 + bob2);
      ctx.restore();
    };

    const drawHonkBubble = (x, yS) => {
      ctx.font = 'bold 12px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillStyle = 'rgba(255,255,255,0.95)';
      ctx.beginPath();
      if (ctx.roundRect) ctx.roundRect(x - 34, yS - 82, 68, 22, 8);
      else ctx.rect(x - 34, yS - 82, 68, 22);
      ctx.fill();
      ctx.fillStyle = '#b45309';
      ctx.fillText('📣 HONK!', x, yS - 66);
    };

    const drawHouse = (n, info, t) => {
      const hp = housePos(n);
      const th = NB[hp.nb];
      const x = hp.x;
      const y = hp.y - camRef.current;
      if (y < -180 || y > H + 60) return;
      // manicured lawn — walk all the way around!
      const lawnCol = ['#16281e', '#3f8b49', '#66722d', '#e6edf5'][hp.nb];
      const lawnTrim = ['#0d1a13', '#2e6b37', '#4d571f', '#c8d6e8'][hp.nb];
      ctx.fillStyle = lawnCol;
      ctx.beginPath();
      if (ctx.roundRect) ctx.roundRect(x - 112, y - 118, 224, 160, 16); else ctx.rect(x - 112, y - 118, 224, 160);
      ctx.fill();
      ctx.strokeStyle = lawnTrim;
      ctx.lineWidth = 5;
      ctx.stroke();
      // mowing stripes
      ctx.fillStyle = 'rgba(255,255,255,0.05)';
      for (let s = 0; s < 4; s++) ctx.fillRect(x - 112 + s * 56, y - 118, 28, 160);
      // stone path from the front door to the street
      const pdir = hp.side ? -1 : 1;
      ctx.fillStyle = hp.nb === 3 ? '#b9c6d6' : '#9aa0a8';
      for (let s = 0; s < 4; s++) {
        ctx.beginPath();
        ctx.ellipse(x + pdir * (78 + s * 26), y + 6, 11, 6, 0, 0, Math.PI * 2);
        ctx.fill();
      }
      // flower bed along the front
      const petals = ['#22d3ee', '#f43f5e', '#fb923c', '#93c5fd'][hp.nb];
      for (let f = 0; f < 5; f++) {
        const fx = x - 56 + f * 28;
        ctx.fillStyle = '#2e6b37';
        ctx.fillRect(fx - 1, y + 33, 2, 6);
        ctx.fillStyle = petals;
        ctx.beginPath(); ctx.arc(fx, y + 32, 3.2, 0, Math.PI * 2); ctx.fill();
      }
      // mailbox at the curb
      const mbx = x + pdir * 104;
      ctx.fillStyle = '#6b4a2c';
      ctx.fillRect(mbx - 2, y - 10, 4, 18);
      ctx.fillStyle = hp.nb === 0 ? '#22d3ee' : '#c0392b';
      ctx.fillRect(mbx - 8, y - 18, 16, 10);
      // soft house shadow
      ctx.fillStyle = 'rgba(0,0,0,0.18)';
      ctx.beginPath(); ctx.ellipse(x, y + 4, 84, 14, 0, 0, Math.PI * 2); ctx.fill();
      // body
      ctx.fillStyle = th.body;
      ctx.fillRect(x - 65, y - 95, 130, 95);
      // roof
      ctx.fillStyle = th.roof;
      ctx.beginPath();
      ctx.moveTo(x - 78, y - 95); ctx.lineTo(x, y - 148); ctx.lineTo(x + 78, y - 95);
      ctx.closePath(); ctx.fill();
      if (hp.nb === 3) { ctx.fillStyle = '#fff'; ctx.fillRect(x - 60, y - 100, 120, 6); }
      if (hp.nb === 0) {
        const hue = (phaseRef.current * 2 + n * 40) % 360;
        ctx.strokeStyle = `hsl(${hue}, 90%, 60%)`;
        ctx.lineWidth = 2.5;
        ctx.strokeRect(x - 65, y - 95, 130, 95);
      }
      // windows
      const winGlow = 0.5 + Math.abs(Math.sin(t * 0.8 + n)) * 0.4;
      ctx.fillStyle = info?.occupied ? `rgba(253,230,138,${winGlow})` : 'rgba(30,30,40,0.8)';
      ctx.fillRect(x - 52, y - 78, 28, 24);
      ctx.fillRect(x + 24, y - 78, 28, 24);
      // door
      ctx.fillStyle = th.door;
      ctx.fillRect(x - 15, y - 48, 30, 48);
      ctx.fillStyle = '#fbbf24';
      ctx.beginPath(); ctx.arc(x + 8, y - 24, 2.5, 0, Math.PI * 2); ctx.fill();
      // number
      ctx.font = 'bold 11px monospace';
      ctx.textAlign = 'center';
      ctx.fillStyle = 'rgba(255,255,255,0.85)';
      ctx.fillText(String(n).padStart(2, '0'), x, y - 55);
      // plaque
      ctx.fillStyle = 'rgba(0,0,0,0.65)';
      ctx.fillRect(x - 62, y + 14, 124, 15);
      ctx.font = 'bold 9px sans-serif';
      if (info?.occupied) {
        ctx.fillStyle = '#FFD700';
        ctx.shadowColor = '#FFD700';
        ctx.shadowBlur = 5;
        ctx.fillText((info.owner_username || '').slice(0, 16).toUpperCase(), x, y + 25);
        ctx.shadowBlur = 0;
      } else {
        ctx.fillStyle = 'rgba(255,255,255,0.7)';
        ctx.fillText('FOR RENT — 250 VC', x, y + 25);
      }
      // theme props
      if (hp.nb === 1) {
        ctx.strokeStyle = '#8a6a3a'; ctx.lineWidth = 4;
        ctx.beginPath(); ctx.moveTo(x - 95, y); ctx.lineTo(x - 95, y - 40); ctx.stroke();
        ctx.fillStyle = '#2e8b57';
        [-1, 0, 1].forEach((d) => {
          ctx.beginPath(); ctx.ellipse(x - 95 + d * 12, y - 46, 14, 5, d * 0.5, 0, Math.PI * 2); ctx.fill();
        });
      }
      if (hp.nb === 2) {
        ctx.fillStyle = '#7a3b12';
        ctx.fillRect(x - 98, y - 26, 6, 26);
        ctx.fillStyle = '#e8742c';
        ctx.beginPath(); ctx.arc(x - 95, y - 36, 16, 0, Math.PI * 2); ctx.fill();
      }
      if (hp.nb === 3) {
        ctx.fillStyle = '#2f5d3a';
        ctx.beginPath(); ctx.moveTo(x - 108, y); ctx.lineTo(x - 95, y - 50); ctx.lineTo(x - 82, y); ctx.closePath(); ctx.fill();
        ctx.fillStyle = '#fff';
        ctx.beginPath(); ctx.moveTo(x - 101, y - 26); ctx.lineTo(x - 95, y - 50); ctx.lineTo(x - 89, y - 26); ctx.closePath(); ctx.fill();
      }
    };

    // cozy street props: hydrants, benches, planters along the block
    const drawStreetProps = () => {
      const props = [
        { kind: 'hydrant', x: 566, y: 700 },
        { kind: 'bench', x: 230, y: 1010 },
        { kind: 'planter', x: 566, y: 1330 },
        { kind: 'bench', x: 566, y: 1720 },
        { kind: 'hydrant', x: 230, y: 2060 },
        { kind: 'planter', x: 230, y: 2410 },
        { kind: 'bench', x: 566, y: 2760 },
        { kind: 'hydrant', x: 566, y: 3060 },
      ];
      props.forEach((p) => {
        const y = p.y - camRef.current;
        if (y < -40 || y > H + 40) return;
        if (p.kind === 'hydrant') {
          ctx.fillStyle = '#c0392b';
          ctx.fillRect(p.x - 5, y - 14, 10, 14);
          ctx.beginPath(); ctx.arc(p.x, y - 14, 5, Math.PI, 0); ctx.fill();
          ctx.fillStyle = '#e74c3c';
          ctx.fillRect(p.x - 8, y - 8, 16, 3);
        } else if (p.kind === 'bench') {
          ctx.fillStyle = '#7a5230';
          ctx.fillRect(p.x - 18, y - 10, 36, 5);
          ctx.fillRect(p.x - 18, y - 20, 36, 4);
          ctx.fillStyle = '#4a3018';
          ctx.fillRect(p.x - 16, y - 5, 4, 6);
          ctx.fillRect(p.x + 12, y - 5, 4, 6);
        } else {
          ctx.fillStyle = '#5b4636';
          ctx.fillRect(p.x - 12, y - 10, 24, 10);
          ctx.fillStyle = '#3e8948';
          ctx.beginPath(); ctx.ellipse(p.x, y - 13, 13, 6, 0, 0, Math.PI * 2); ctx.fill();
          ctx.fillStyle = '#f43f5e';
          [-6, 0, 6].forEach((d) => { ctx.beginPath(); ctx.arc(p.x + d, y - 15, 2, 0, Math.PI * 2); ctx.fill(); });
        }
      });
    };

    // rotated car: cx/wy = center in world coords, angle = heading (default up)
    const drawCar = (car, cx, wy, angle = -Math.PI / 2) => {
      const y = wy - camRef.current;
      if (y < -140 || y > H + 140) return;
      const len = car.limo ? 108 : 64;
      const wid = 36;
      ctx.save();
      ctx.translate(cx, y);
      // shadow (always under the car)
      ctx.fillStyle = 'rgba(0,0,0,0.3)';
      ctx.beginPath(); ctx.ellipse(0, 5, len / 2.3, 14, 0, 0, Math.PI * 2); ctx.fill();
      ctx.rotate(angle + Math.PI / 2);
      const y0 = -len / 2;
      // wheels
      ctx.fillStyle = '#101014';
      ctx.fillRect(-wid / 2 - 4, y0 + 6, 5, 15);
      ctx.fillRect(wid / 2 - 1, y0 + 6, 5, 15);
      ctx.fillRect(-wid / 2 - 4, y0 + len - 22, 5, 15);
      ctx.fillRect(wid / 2 - 1, y0 + len - 22, 5, 15);
      // body
      ctx.fillStyle = car.color;
      ctx.beginPath();
      if (ctx.roundRect) ctx.roundRect(-wid / 2, y0, wid, len, 9);
      else ctx.rect(-wid / 2, y0, wid, len);
      ctx.fill();
      ctx.strokeStyle = 'rgba(0,0,0,0.35)';
      ctx.lineWidth = 1.5;
      ctx.stroke();
      // windshield + rear window
      ctx.fillStyle = car.convertible ? '#4a3520' : '#0e1420';
      ctx.fillRect(-wid / 2 + 5, y0 + 10, wid - 10, 13);
      if (!car.convertible) ctx.fillRect(-wid / 2 + 5, y0 + len - 22, wid - 10, 11);
      // roof / interior
      if (car.convertible) {
        ctx.fillStyle = '#2a1d10';
        ctx.fillRect(-wid / 2 + 6, y0 + 26, wid - 12, len - 40);
        ctx.fillStyle = '#6b4a2a';
        ctx.fillRect(-wid / 2 + 8, y0 + 29, wid - 16, 8);
        ctx.fillRect(-wid / 2 + 8, y0 + 41, wid - 16, 8);
      } else {
        ctx.fillStyle = car.trim;
        ctx.globalAlpha = 0.25;
        ctx.fillRect(-wid / 2 + 6, y0 + 26, wid - 12, len - 50);
        ctx.globalAlpha = 1;
      }
      if (car.limo) {
        ctx.strokeStyle = '#FFD700';
        ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.moveTo(-wid / 2 + 3, 0); ctx.lineTo(wid / 2 - 3, 0); ctx.stroke();
      }
      if (car.snowy) {
        ctx.fillStyle = 'rgba(255,255,255,0.85)';
        ctx.fillRect(-wid / 2 + 4, y0 + 24, wid - 8, 7);
      }
      // blinking alarm light
      if (Math.floor(phaseRef.current / 45 + (car.y || 0)) % 2 === 0) {
        ctx.fillStyle = '#ef4444';
        ctx.beginPath(); ctx.arc(0, y0 + len - 6, 2, 0, Math.PI * 2); ctx.fill();
      }
      ctx.restore();
    };

    const drawNPCs = () => {
      NPCS.forEach((npc) => {
        const span = npc.yMax - npc.yMin;
        const tt = (phaseRef.current * npc.speed + npc.phase) % (span * 2);
        const wy = tt < span ? npc.yMin + tt : npc.yMax - (tt - span);
        const dir = tt < span ? 'down' : 'up';
        // sidestep in an arc around parked cars (wherever they were left) 
        let nx = npc.x;
        for (const st of carsRef.current) {
          if (Math.abs(st.x - npc.x) > 46) continue;
          const clen = CARS[st.idx]?.limo ? 108 : 64;
          const d = wy - st.y;
          const range = clen / 2 + 40;
          if (Math.abs(d) < range) nx += (st.x > 400 ? -34 : 34) * Math.cos((d / range) * (Math.PI / 2));
        }
        // skirt around the Ferris wheel footprint (left sidewalk only)
        if (npc.x === 270) {
          const fd = wy - FERRIS.y;
          const fRange = FERRIS.r + 58;
          if (Math.abs(fd) < fRange) nx += 64 * Math.cos((fd / fRange) * (Math.PI / 2));
        }
        const y = wy - camRef.current;
        if (y < -60 || y > H + 40) return;
        ensureSpriteLoaded(npc.char);
        drawPlayerSprite(ctx, nx, y, dir, true, phaseRef.current * 0.18 + npc.phase, 1.15, { hue: npc.hue, sat: 100, bright: 100 }, npc.char);
      });
      // district strollers (walk east-west)
      H_NPCS.forEach((npc) => {
        const span = npc.xMax - npc.xMin;
        const tt = (phaseRef.current * npc.speed + npc.phase) % (span * 2);
        const wx = tt < span ? npc.xMin + tt : npc.xMax - (tt - span);
        const dir = tt < span ? 'right' : 'left';
        const y = npc.y - camRef.current;
        if (y < -60 || y > H + 40) return;
        ensureSpriteLoaded(npc.char);
        drawPlayerSprite(ctx, wx, y, dir, true, phaseRef.current * 0.18 + npc.phase, 1.15, { hue: 0, sat: 100, bright: 100 }, npc.char);
      });
    };

    const drawWaitress = (x, y, walking, ph) => {
      const step = walking ? Math.sin(ph * 0.35) * 4 : Math.sin(ph * 0.04) * 1.2;
      ctx.fillStyle = '#e8c39e';
      ctx.fillRect(x - 6, y - 18, 4, 18 + step * 0.5);
      ctx.fillRect(x + 2, y - 18, 4, 18 - step * 0.5);
      ctx.fillStyle = '#2dd4bf';
      ctx.beginPath();
      ctx.moveTo(x - 12, y - 16); ctx.lineTo(x + 12, y - 16); ctx.lineTo(x + 8, y - 42); ctx.lineTo(x - 8, y - 42);
      ctx.closePath(); ctx.fill();
      ctx.fillStyle = '#f8fafc';
      ctx.beginPath();
      ctx.moveTo(x - 8, y - 18); ctx.lineTo(x + 8, y - 18); ctx.lineTo(x + 6, y - 34); ctx.lineTo(x - 6, y - 34);
      ctx.closePath(); ctx.fill();
      ctx.fillStyle = '#e8c39e';
      ctx.fillRect(x - 14, y - 40, 4, 14);
      ctx.fillRect(x + 10, y - 44, 4, 12);
      ctx.fillStyle = '#c0c5ce';
      ctx.beginPath(); ctx.ellipse(x + 15, y - 45, 9, 3, 0, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#f1c9a5';
      ctx.beginPath(); ctx.arc(x, y - 50, 8, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#5b3a1e';
      ctx.beginPath(); ctx.arc(x, y - 55, 7, Math.PI, 0); ctx.fill();
      ctx.beginPath(); ctx.arc(x + 6, y - 59, 4, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#3a2a1a';
      ctx.fillRect(x - 3.5, y - 51, 1.8, 1.8); ctx.fillRect(x + 1.7, y - 51, 1.8, 1.8);
      ctx.strokeStyle = '#b05a4a'; ctx.lineWidth = 1;
      ctx.beginPath(); ctx.arc(x, y - 47, 2.5, 0.2, Math.PI - 0.2); ctx.stroke();
      ctx.font = '8px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillStyle = 'rgba(255,255,255,0.75)';
      ctx.fillText('Shelly ✿', x, y + 10);
    };

    const drawDinerScene = (t) => {
      // ocean-view window
      ctx.fillStyle = '#0e3a5c';
      ctx.fillRect(280, 70, 240, 140);
      ctx.fillStyle = '#1a5c8a';
      ctx.fillRect(280, 140, 240, 70);
      ctx.strokeStyle = 'rgba(255,255,255,0.5)'; ctx.lineWidth = 1.5;
      for (let i = 0; i < 3; i++) {
        const wx = 300 + ((t * 0.4 + i * 80) % 200);
        ctx.beginPath(); ctx.arc(wx, 152 + i * 18, 8, Math.PI, 0); ctx.stroke();
      }
      ctx.fillStyle = '#fde047';
      ctx.beginPath(); ctx.arc(460, 105, 16, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = '#6b4a2a'; ctx.lineWidth = 6;
      ctx.strokeRect(280, 70, 240, 140);
      // bar counter (left)
      ctx.fillStyle = '#6b4a2a'; ctx.fillRect(40, 192, 190, 24);
      ctx.fillStyle = '#4a3018'; ctx.fillRect(40, 216, 190, 58);
      ['#34d399', '#38bdf8', '#fbbf24', '#f472b6'].forEach((c2, i) => {
        ctx.fillStyle = c2; ctx.fillRect(56 + i * 40, 162, 10, 28);
      });
      // menu board
      ctx.fillStyle = '#131313'; ctx.fillRect(556, 78, 196, 148);
      ctx.strokeStyle = '#6b4a2a'; ctx.lineWidth = 3; ctx.strokeRect(556, 78, 196, 148);
      ctx.font = 'bold 10px monospace';
      ctx.textAlign = 'left';
      ctx.fillStyle = '#fef3c7'; ctx.fillText('🍹 Piña Colada ..... 12', 564, 94);
      ctx.fillStyle = '#fbbf24'; ctx.fillText('🍺 Beach Beer ....... 8', 564, 107);
      ctx.fillStyle = '#38bdf8'; ctx.fillText('🍏 Blue Apple ...... 10', 564, 120);
      ctx.fillStyle = '#34d399'; ctx.fillText('🍸 Turtlelini ...... 15', 564, 133);
      ctx.strokeStyle = 'rgba(255,255,255,0.25)'; ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(564, 142); ctx.lineTo(744, 142); ctx.stroke();
      ctx.fillStyle = '#f59e0b'; ctx.fillText('🍔 Shack Burger .... 18', 564, 158);
      ctx.fillStyle = '#fde047'; ctx.fillText('🍟 Boardwalk Fries .. 9', 564, 171);
      ctx.fillStyle = '#fb923c'; ctx.fillText('🍤 Coconut Shrimp .. 16', 564, 184);
      ctx.fillStyle = '#a3e635'; ctx.fillText('🥧 Key Lime Pie .... 12', 564, 197);
      // string lights
      for (let i = 0; i < 12; i++) {
        const on2 = (Math.floor(t / 14) + i) % 3 === 0;
        ctx.fillStyle = on2 ? '#fde047' : 'rgba(253,224,71,0.3)';
        ctx.beginPath(); ctx.arc(70 + i * 60, 58 + Math.sin(i * 1.2) * 6, 3, 0, Math.PI * 2); ctx.fill();
      }
      // two long tables, four seats each — date-night ready
      DINER_TABLES.forEach((tb, i) => {
        ctx.fillStyle = 'rgba(0,0,0,0.15)';
        ctx.beginPath(); ctx.ellipse(tb.x, tb.y + 18, 66, 14, 0, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = '#7a5230';
        ctx.fillRect(tb.x - 48, tb.y - 22, 96, 44);
        ctx.fillStyle = '#8f6238';
        ctx.fillRect(tb.x - 52, tb.y - 28, 104, 8);
        ctx.fillStyle = 'rgba(255,255,255,0.08)';
        ctx.fillRect(tb.x - 44, tb.y - 16, 88, 4);
        ctx.strokeStyle = '#d1d5db'; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(tb.x, tb.y - 28); ctx.lineTo(tb.x, tb.y - 56); ctx.stroke();
        ctx.fillStyle = ['#f472b6', '#38bdf8'][i];
        ctx.beginPath(); ctx.arc(tb.x, tb.y - 56, 16, Math.PI, 0); ctx.fill();
        ctx.fillStyle = 'rgba(255,255,255,0.35)';
        ctx.beginPath(); ctx.arc(tb.x, tb.y - 56, 16, Math.PI + 0.4, Math.PI + 1); ctx.fill();
      });
      DINER_SEATS.forEach((s2) => {
        ctx.fillStyle = '#b3452e';
        ctx.beginPath(); ctx.ellipse(s2.x, s2.y - 4, 15, 8, 0, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = 'rgba(255,255,255,0.16)';
        ctx.beginPath(); ctx.ellipse(s2.x, s2.y - 6, 9, 4, 0, 0, Math.PI * 2); ctx.fill();
      });
      ctx.font = 'bold 10px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillStyle = 'rgba(255,255,255,0.6)';
      ctx.fillText('tap a seat — every table sits four 🪑', 400, 250);
      // waitress state machine
      const d = dinerRef.current;
      const HOME = { x: 135, y: 306 };
      if (d) {
        const seat = DINER_SEATS[d.seat];
        const tgt = { x: seat.x + (seat.wox || 34), y: seat.y + (seat.woy || 0) };
        const el = Date.now() - d.t0;
        let wx2 = HOME.x, wy2 = HOME.y, wWalking = false, bubble = null;
        if (d.stage === 'coming') {
          const p = Math.min(1, el / 1400);
          wx2 = HOME.x + (tgt.x - HOME.x) * p; wy2 = HOME.y + (tgt.y - HOME.y) * p; wWalking = p < 1;
          if (p >= 1) { d.stage = 'asking'; d.t0 = Date.now(); setShowMenu(true); }
        } else if (d.stage === 'asking') {
          wx2 = tgt.x; wy2 = tgt.y;
          bubble = 'What can I get started for you today?';
        } else if (d.stage === 'fetching') {
          const p = Math.min(1, el / 1200);
          wx2 = tgt.x + (HOME.x - tgt.x) * p; wy2 = tgt.y + (HOME.y - tgt.y) * p; wWalking = p < 1;
          if (p >= 1) { d.stage = 'returning'; d.t0 = Date.now(); }
        } else if (d.stage === 'returning') {
          const p = Math.min(1, el / 1200);
          wx2 = HOME.x + (tgt.x - HOME.x) * p; wy2 = HOME.y + (tgt.y - HOME.y) * p; wWalking = p < 1;
          if (p >= 1) {
            d.stage = 'served'; d.t0 = Date.now();
            toast.success(`${d.drink.icon} Enjoy your ${d.drink.name}!`);
            activateDrinkFx(d.drink);
          }
        } else if (d.stage === 'served') {
          if (el < 1100) { wx2 = tgt.x; wy2 = tgt.y; bubble = 'Enjoy! 💚'; }
          else {
            const p = Math.min(1, (el - 1100) / 1200);
            wx2 = tgt.x + (HOME.x - tgt.x) * p; wy2 = tgt.y + (HOME.y - tgt.y) * p; wWalking = p < 1;
            if (p >= 1) d.stage = 'idle_served';
          }
        }
        if ((d.stage === 'served' || d.stage === 'idle_served') && d.drink) {
          ctx.font = '17px sans-serif'; ctx.textAlign = 'center';
          ctx.fillText(d.drink.icon, (seat.x + seat.tx) / 2, (seat.y + seat.ty) / 2 - 8);
        }
        drawWaitress(wx2, wy2, wWalking, phaseRef.current);
        if (bubble) {
          ctx.font = 'bold 11px sans-serif';
          ctx.textAlign = 'center';
          const bw = ctx.measureText(bubble).width + 18;
          ctx.fillStyle = 'rgba(255,255,255,0.94)';
          ctx.beginPath();
          if (ctx.roundRect) ctx.roundRect(Math.min(Math.max(wx2 - bw / 2, 6), 794 - bw), wy2 - 94, bw, 22, 8);
          else ctx.rect(Math.min(Math.max(wx2 - bw / 2, 6), 794 - bw), wy2 - 94, bw, 22);
          ctx.fill();
          ctx.fillStyle = '#14532d';
          ctx.fillText(bubble, Math.min(Math.max(wx2, 6 + bw / 2), 794 - bw / 2), wy2 - 79);
        }
      } else {
        drawWaitress(HOME.x, HOME.y, false, phaseRef.current);
      }
    };

    const drawStreet = () => {
      const cam = camRef.current;
      const t = phaseRef.current * 0.05;
      // sky/base
      ctx.fillStyle = '#0b0a14';
      ctx.fillRect(0, 0, W, H);
      // ===== world space (city extends into side districts; camera pans X too) =====
      ctx.save();
      ctx.translate(-camXRef.current, 0);
      // neighborhood grounds
      for (let nb = 0; nb < 4; nb++) {
        const sy = 400 + nb * 640 - cam;
        if (sy > H || sy + 640 < 0) continue;
        ctx.fillStyle = NB[nb].ground;
        ctx.fillRect(-1400, sy, 3600, 640);
      }
      // mansion district ground
      const my = 2960 - cam;
      if (my < H && my + 1660 > 0) {
        ctx.fillStyle = '#1a1f16';
        ctx.fillRect(-1400, my, 3600, 1660);
      }
      // fairgrounds ground
      const fgy = 4620 - cam;
      if (fgy < H && fgy + 940 > 0) {
        ctx.fillStyle = '#1c1426';
        ctx.fillRect(-1400, fgy, 3600, 940);
        // scattered confetti
        for (let i = 0; i < 30; i++) {
          const cfy = fgy + ((i * 127) % 940);
          if (cfy > -5 && cfy < H + 5) {
            ctx.fillStyle = ['#f472b6', '#38bdf8', '#fde047', '#34d399'][i % 4];
            ctx.fillRect((i * 211) % W, cfy, 3, 3);
          }
        }
      }
      // road + sidewalks
      ctx.fillStyle = '#3f4048';
      ctx.fillRect(240, 0, 60, H);
      ctx.fillRect(500, 0, 60, H);
      ctx.fillStyle = '#26262c';
      ctx.fillRect(300, 0, 200, H);
      ctx.strokeStyle = 'rgba(250,204,21,0.8)';
      ctx.lineWidth = 3;
      ctx.setLineDash([26, 22]);
      ctx.beginPath(); ctx.moveTo(400, -((cam) % 48)); ctx.lineTo(400, H); ctx.stroke();
      ctx.setLineDash([]);
      // sidewalk seams
      ctx.strokeStyle = 'rgba(255,255,255,0.08)';
      ctx.lineWidth = 1;
      for (let sy = -(cam % 60); sy < H; sy += 60) {
        ctx.beginPath(); ctx.moveTo(240, sy); ctx.lineTo(300, sy); ctx.moveTo(500, sy); ctx.lineTo(560, sy); ctx.stroke();
      }
      // streetlights
      for (let ly = 520 - (cam % 320); ly < H + 80; ly += 320) {
        [296, 504].forEach((lx) => {
          ctx.strokeStyle = '#3a3a44'; ctx.lineWidth = 4;
          ctx.beginPath(); ctx.moveTo(lx, ly); ctx.lineTo(lx, ly - 62); ctx.stroke();
          const gl = 0.6 + Math.abs(Math.sin(t + lx + ly)) * 0.4;
          ctx.fillStyle = `rgba(253,230,138,${gl})`;
          ctx.beginPath(); ctx.arc(lx, ly - 66, 5, 0, Math.PI * 2); ctx.fill();
          ctx.fillStyle = `rgba(253,230,138,${gl * 0.1})`;
          ctx.beginPath(); ctx.arc(lx, ly - 60, 34, 0, Math.PI * 2); ctx.fill();
        });
      }
      // ===== SIDE DISTRICTS + CROSS STREETS + MANHOLES =====
      drawDistricts(ctx, cam, phaseRef.current, t);
      if (jetskiRef.current && !inWater(posRef.current.x, posRef.current.y)) {
        jetskiRef.current = false;
        toast.success('🏖️ Jet-ski parked — nice ride!');
      }
      drawBeachZone(ctx, cam, phaseRef.current, {
        liveRoom: liveScreenRef.current,
        coverImg: liveCoverImgRef.current,
        video: screenVideoRef.current,
        volley: volleyRef.current,
        jetskiTaken: jetskiRef.current,
      });
      drawGodsTower(ctx, cam, phaseRef.current);
      // Randall's gated plot — opens when someone walks or drives up
      {
        const gp = drivingRef.current || posRef.current;
        const nearGate = Math.abs(gp.x - GODS_GATE.x) < 140 && Math.abs(gp.y - GODS_GATE.y) < 140;
        gateRef.current += ((nearGate ? 1 : 0) - gateRef.current) * 0.07;
        drawGodsGate(ctx, cam, phaseRef.current, gateRef.current);
      }
      // rocket launch pad → the Moon
      {
        let launchP = 0;
        if (launchRef.current) {
          launchP = Math.min(1, (Date.now() - launchRef.current.t0) / 1600);
          if (launchP >= 1) { launchRef.current = null; enterMoonSurface(); toast.success('🌕 Welcome to the Moon!'); }
        }
        const rk = drawRocketPad(ctx, cam, phaseRef.current, launchP);
        // your character rides in the porthole during liftoff 🚀
        if (launchP > 0 && launchP < 1 && rk) {
          const px2 = rk.x, py2 = rk.y - 8 * rk.s;
          ctx.save();
          ctx.beginPath(); ctx.arc(px2, py2, 6.6 * rk.s, 0, Math.PI * 2); ctx.clip();
          if (isSheetSpecial(avatar?.special)) {
            drawSpecialSprite(ctx, avatar.special, px2, py2 + 26, 0.55, 'down', false, 0);
          } else {
            drawPlayerSprite(ctx, px2, py2 + 26, 'down', false, 0, 0.62, computeTint(avatar), resolveGender(avatar?.gender));
          }
          ctx.restore();
          ctx.strokeStyle = '#94a3b8'; ctx.lineWidth = 2 * rk.s;
          ctx.beginPath(); ctx.arc(px2, py2, 7 * rk.s, 0, Math.PI * 2); ctx.stroke();
        }
      }
      // ===== CLUB FRONT (top of block) =====
      const cy0 = -cam;
      if (cy0 + 300 > 0) {
        // skyline behind
        ctx.fillStyle = '#12101e';
        ctx.fillRect(0, cy0, W, 60);
        [[20, 34], [70, 50], [640, 44], [720, 56]].forEach(([bx, bh]) => {
          ctx.fillStyle = '#191627';
          ctx.fillRect(bx, cy0 + 60 - bh, 40, bh);
        });
        // club building
        ctx.fillStyle = '#181322';
        ctx.fillRect(180, cy0 + 40, 440, 250);
        ctx.strokeStyle = '#e11d8f';
        ctx.lineWidth = 3;
        ctx.strokeRect(180, cy0 + 40, 440, 250);
        // marquee
        ctx.fillStyle = '#0c0812';
        ctx.fillRect(210, cy0 + 70, 380, 64);
        ctx.strokeStyle = '#fbbf24';
        ctx.lineWidth = 2;
        ctx.strokeRect(210, cy0 + 70, 380, 64);
        for (let i = 0; i < 18; i++) {
          const on = (Math.floor(phaseRef.current / 12) + i) % 3 === 0;
          ctx.fillStyle = on ? '#fde047' : '#5a4a12';
          ctx.beginPath(); ctx.arc(222 + i * 21, cy0 + 78, 3, 0, Math.PI * 2); ctx.fill();
          ctx.beginPath(); ctx.arc(222 + i * 21, cy0 + 126, 3, 0, Math.PI * 2); ctx.fill();
        }
        const mg = 0.7 + Math.abs(Math.sin(t * 1.4)) * 0.3;
        ctx.font = 'bold 26px monospace';
        ctx.textAlign = 'center';
        ctx.fillStyle = '#f0abfc';
        ctx.shadowColor = '#e11d8f';
        ctx.shadowBlur = 18 * mg;
        ctx.fillText('RAZIENTSOUNDS', 400, cy0 + 110);
        ctx.shadowBlur = 0;
        // club doors + carpet
        ctx.fillStyle = '#2d1d3d';
        ctx.fillRect(346, cy0 + 190, 52, 100);
        ctx.fillRect(402, cy0 + 190, 52, 100);
        ctx.strokeStyle = '#fbbf24'; ctx.lineWidth = 2;
        ctx.strokeRect(346, cy0 + 190, 52, 100);
        ctx.strokeRect(402, cy0 + 190, 52, 100);
        ctx.fillStyle = 'rgba(150,20,30,0.9)';
        ctx.fillRect(340, cy0 + 292, 120, 16);
        ctx.font = 'bold 11px sans-serif';
        ctx.fillStyle = '#fde68a';
        ctx.fillText('🚪 ENTER CLUB', 400, cy0 + 178);
        // velvet ropes
        [326, 474].forEach((rx) => {
          ctx.fillStyle = '#c9a227';
          ctx.beginPath(); ctx.arc(rx, cy0 + 296, 4, 0, Math.PI * 2); ctx.fill();
          ctx.fillRect(rx - 2, cy0 + 296, 4, 22);
        });
        // apartment tower (right)
        ctx.fillStyle = '#241a10';
        ctx.fillRect(630, cy0 + 20, 170, 270);
        ctx.strokeStyle = '#fbbf24'; ctx.lineWidth = 2;
        ctx.strokeRect(630, cy0 + 20, 170, 270);
        for (let r = 0; r < 5; r++) {
          for (let c = 0; c < 3; c++) {
            const on = ((r * 3 + c + Math.floor(phaseRef.current / 90)) % 4) !== 0;
            ctx.fillStyle = on ? 'rgba(253,230,138,0.75)' : 'rgba(30,25,15,0.9)';
            ctx.fillRect(646 + c * 50, cy0 + 40 + r * 40, 30, 24);
          }
        }
        ctx.fillStyle = '#3a2a16';
        ctx.fillRect(692, cy0 + 240, 46, 50);
        ctx.font = 'bold 11px monospace';
        ctx.fillStyle = '#fbbf24';
        ctx.shadowColor = '#fbbf24';
        ctx.shadowBlur = 8;
        ctx.fillText('🏢 APARTMENTS', 715, cy0 + 14);
        ctx.shadowBlur = 0;
      }
      // ===== NEIGHBORHOOD BANNERS + THEME FX =====
      for (let nb = 0; nb < 4; nb++) {
        const by = 400 + nb * 640 + 60 - cam;
        if (by > -40 && by < H + 40) {
          ctx.fillStyle = 'rgba(0,0,0,0.55)';
          ctx.fillRect(250, by - 18, 300, 26);
          ctx.font = 'bold 15px monospace';
          ctx.textAlign = 'center';
          ctx.fillStyle = NB[nb].accent;
          ctx.shadowColor = NB[nb].accent;
          ctx.shadowBlur = 10;
          ctx.fillText(NB[nb].name, 400, by + 1);
          ctx.shadowBlur = 0;
        }
      }
      // snow in Snowfall Pines
      const snowTop = 400 + 3 * 640 - cam;
      if (snowTop < H && snowTop + 640 > 0) {
        for (let i = 0; i < 26; i++) {
          const fy = snowTop + ((phaseRef.current * 0.7 + i * 47) % 640);
          if (fy > 0 && fy < H) {
            ctx.fillStyle = 'rgba(255,255,255,0.8)';
            ctx.beginPath();
            ctx.arc((i * 73) % W + Math.sin(t + i) * 10, fy, 2, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      }
      // houses
      for (let n = 1; n <= 16; n++) {
        drawHouse(n, houses.find((h) => h.house_number === n), t);
      }
      // parked cars (they stay wherever they were left) + strolling locals
      carsRef.current.forEach((st, ci) => {
        if (drivingRef.current?.idx === ci) return;
        if (st.driverSid && st.driverSid !== 'me') {
          const live = Object.values(playersRef.current || {}).some(
            (q) => (q.room || 'main') === 'city' && q.transform && q.transform.car === ci
          );
          if (live) return; // drawn under its live driver instead
        }
        drawCar(CARS[ci], st.x, st.y, st.angle);
      });
      drawNPCs();
      drawStreetProps();
      // ===== MANSION DISTRICT =====
      const gy = 3020 - cam;
      if (gy > -2600 && gy < H + 100) {
        // gates banner
        ctx.fillStyle = 'rgba(0,0,0,0.55)';
        ctx.fillRect(230, gy - 18, 340, 26);
        ctx.font = 'bold 15px monospace';
        ctx.textAlign = 'center';
        ctx.fillStyle = '#FFD700';
        ctx.shadowColor = '#FFD700';
        ctx.shadowBlur = 10;
        ctx.fillText('✦ MILLIONAIRE ROW ✦', 400, gy + 1);
        ctx.shadowBlur = 0;
        // fountain
        const fy2 = 3480 - cam;
        ctx.fillStyle = '#3b4252';
        ctx.beginPath(); ctx.ellipse(400, fy2, 56, 20, 0, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = '#38bdf8';
        ctx.beginPath(); ctx.ellipse(400, fy2, 44, 14, 0, 0, Math.PI * 2); ctx.fill();
        const spray = Math.abs(Math.sin(t * 2)) * 18;
        ctx.strokeStyle = 'rgba(147,197,253,0.8)';
        ctx.lineWidth = 3;
        ctx.beginPath(); ctx.moveTo(400, fy2 - 4); ctx.lineTo(400, fy2 - 18 - spray); ctx.stroke();
        // reusable big mansion (grand / elite / admin)
        const drawBigMansion = (n, mx, myy, theme) => {
          const info = houses.find((h) => h.house_number === n);
          const yy = myy - cam;
          if (yy < -240 || yy > H + 240) return;
          ctx.fillStyle = theme.wall;
          ctx.fillRect(mx - 105, yy - 150, 210, 150);
          ctx.fillStyle = theme.roof;
          ctx.beginPath(); ctx.moveTo(mx - 118, yy - 150); ctx.lineTo(mx, yy - 205); ctx.lineTo(mx + 118, yy - 150); ctx.closePath(); ctx.fill();
          ctx.strokeStyle = theme.accent; ctx.lineWidth = 2.5;
          ctx.strokeRect(mx - 105, yy - 150, 210, 150);
          [-70, -28, 42, 84].forEach((cxo) => {
            ctx.fillStyle = theme.column;
            ctx.fillRect(mx + cxo - 7, yy - 140, 14, 140);
          });
          ctx.fillStyle = '#3a2a16';
          ctx.fillRect(mx - 20, yy - 66, 40, 66);
          const bmGlow = 0.5 + Math.abs(Math.sin(t * 1.2 + n)) * 0.4;
          ctx.fillStyle = `rgba(253,230,138,${bmGlow})`;
          ctx.fillRect(mx - 88, yy - 120, 26, 30); ctx.fillRect(mx + 62, yy - 120, 26, 30);
          ctx.font = 'bold 12px serif';
          ctx.textAlign = 'center';
          ctx.fillStyle = theme.accent;
          ctx.shadowColor = theme.accent; ctx.shadowBlur = 8;
          ctx.fillText(MANSION_TITLES[n], mx, yy - 158);
          ctx.shadowBlur = 0;
          ctx.fillStyle = 'rgba(0,0,0,0.65)';
          ctx.fillRect(mx - 80, yy + 8, 160, 16);
          ctx.font = 'bold 10px sans-serif';
          if (info?.occupied) {
            ctx.fillStyle = theme.accent;
            ctx.shadowColor = theme.accent; ctx.shadowBlur = 5;
            ctx.fillText((info.owner_username || '').slice(0, 16).toUpperCase(), mx, yy + 20);
            ctx.shadowBlur = 0;
          } else {
            ctx.fillStyle = 'rgba(255,255,255,0.75)';
            ctx.fillText(theme.vacant, mx, yy + 20);
          }
        };
        const GOLD_T = { wall: '#2e2438', roof: '#1e1826', column: '#c8b98a', accent: '#FFD700', vacant: 'FOR RENT — 1,000 VC' };
        const ADMIN_T = { wall: '#16202e', roof: '#0d141e', column: '#7a94b8', accent: '#38bdf8', vacant: isStaff ? 'CLAIM FREE — 7 DAYS 🛡️' : 'STAFF ONLY 🛡️' };
        drawBigMansion(MANSION_NUM, 140, 3330, GOLD_T);
        // ELITE ROW
        const ery = 3700 - cam;
        if (ery > -40 && ery < H + 40) {
          ctx.fillStyle = 'rgba(0,0,0,0.55)';
          ctx.fillRect(250, ery - 18, 300, 26);
          ctx.font = 'bold 15px monospace';
          ctx.textAlign = 'center';
          ctx.fillStyle = '#FFD700';
          ctx.shadowColor = '#FFD700'; ctx.shadowBlur = 10;
          ctx.fillText('✦ ELITE ROW ✦', 400, ery + 1);
          ctx.shadowBlur = 0;
        }
        drawBigMansion(18, 140, 3960, GOLD_T);
        drawBigMansion(19, 660, 3960, GOLD_T);
        // ADMIN AVENUE
        const aay = 4140 - cam;
        if (aay > -40 && aay < H + 40) {
          ctx.fillStyle = 'rgba(0,0,0,0.55)';
          ctx.fillRect(250, aay - 18, 300, 26);
          ctx.font = 'bold 15px monospace';
          ctx.textAlign = 'center';
          ctx.fillStyle = '#38bdf8';
          ctx.shadowColor = '#38bdf8'; ctx.shadowBlur = 10;
          ctx.fillText('🛡️ ADMIN AVENUE 🛡️', 400, aay + 1);
          ctx.shadowBlur = 0;
        }
        drawBigMansion(20, 140, 4400, ADMIN_T);
        drawBigMansion(21, 660, 4400, ADMIN_T);
        // OWNER 5-STORY MANSION (right)
        const ox = 660; const omy = 3480 - cam;
        ctx.fillStyle = '#231a30';
        ctx.fillRect(ox - 95, omy - 330, 190, 330);
        ctx.strokeStyle = '#FFD700'; ctx.lineWidth = 3;
        ctx.strokeRect(ox - 95, omy - 330, 190, 330);
        for (let fl = 0; fl < 5; fl++) {
          const fyy = omy - 310 + fl * 62;
          ctx.strokeStyle = 'rgba(255,215,0,0.35)'; ctx.lineWidth = 1;
          ctx.beginPath(); ctx.moveTo(ox - 95, fyy + 52); ctx.lineTo(ox + 95, fyy + 52); ctx.stroke();
          [-58, -6, 46].forEach((wxo) => {
            const on = ((fl * 3 + wxo + Math.floor(phaseRef.current / 70)) % 4) !== 0;
            ctx.fillStyle = on ? 'rgba(253,230,138,0.8)' : 'rgba(25,20,35,0.9)';
            ctx.fillRect(ox + wxo, fyy + 10, 24, 30);
          });
        }
        ctx.fillStyle = '#3a2a16';
        ctx.fillRect(ox - 24, omy - 60, 48, 60);
        ctx.strokeStyle = '#FFD700'; ctx.lineWidth = 2;
        ctx.strokeRect(ox - 24, omy - 60, 48, 60);
        ctx.font = '22px serif';
        ctx.fillText('👑', ox, omy - 340);
        ctx.font = 'bold 11px serif';
        ctx.fillStyle = '#FFD700';
        ctx.shadowColor = '#FFD700'; ctx.shadowBlur = 8;
        ctx.fillText('THE RAZIENT ESTATE', ox, omy - 362);
        ctx.shadowBlur = 0;
        ctx.fillStyle = 'rgba(0,0,0,0.65)';
        ctx.fillRect(ox - 80, omy + 8, 160, 16);
        ctx.font = 'bold 10px serif';
        if (ownerMansion?.occupied) {
          ctx.fillStyle = '#FFD700';
          ctx.shadowColor = '#FFD700'; ctx.shadowBlur = 5;
          ctx.fillText((ownerMansion.owner_username || '').slice(0, 16).toUpperCase(), ox, omy + 20);
          ctx.shadowBlur = 0;
        } else {
          ctx.fillStyle = 'rgba(255,255,255,0.75)';
          ctx.fillText(isStaff ? 'CLAIM ME, BOSS 👑' : 'THE BOSS ONLY', ox, omy + 20);
        }
      }
      // ===== FAIRGROUNDS =====
      const fby = 4680 - cam;
      if (fby > -40 && fby < H + 40) {
        ctx.fillStyle = 'rgba(0,0,0,0.55)';
        ctx.fillRect(230, fby - 18, 340, 26);
        ctx.font = 'bold 15px monospace';
        ctx.textAlign = 'center';
        ctx.fillStyle = '#f472b6';
        ctx.shadowColor = '#f472b6'; ctx.shadowBlur = 10;
        ctx.fillText('🎡 RAZIENT FAIRGROUNDS 🎡', 400, fby + 1);
        ctx.shadowBlur = 0;
      }
      // Ferris wheel
      const fwy = FERRIS.y - cam;
      if (fwy > -340 && fwy < H + 340) {
        const ang = ferrisAngle();
        ctx.strokeStyle = '#6d6875'; ctx.lineWidth = 8;
        ctx.beginPath(); ctx.moveTo(FERRIS.x - 70, fwy + 160); ctx.lineTo(FERRIS.x, fwy); ctx.lineTo(FERRIS.x + 70, fwy + 160); ctx.stroke();
        ctx.fillStyle = '#3a3542';
        ctx.fillRect(FERRIS.x - 92, fwy + 156, 184, 12);
        ctx.lineWidth = 5;
        ctx.strokeStyle = '#8d99ae';
        ctx.beginPath(); ctx.arc(FERRIS.x, fwy, FERRIS.r, 0, Math.PI * 2); ctx.stroke();
        ctx.lineWidth = 2;
        ctx.beginPath(); ctx.arc(FERRIS.x, fwy, FERRIS.r - 12, 0, Math.PI * 2); ctx.stroke();
        for (let i = 0; i < 8; i++) {
          const a = ang + (i * Math.PI) / 4;
          ctx.strokeStyle = '#8d99ae'; ctx.lineWidth = 2.5;
          ctx.beginPath(); ctx.moveTo(FERRIS.x, fwy); ctx.lineTo(FERRIS.x + Math.cos(a) * FERRIS.r, fwy + Math.sin(a) * FERRIS.r); ctx.stroke();
        }
        for (let i = 0; i < 16; i++) {
          const a = (i * Math.PI) / 8;
          const on = (Math.floor(phaseRef.current / 14) + i) % 4 === 0;
          ctx.fillStyle = on ? '#fde047' : 'rgba(253,224,71,0.25)';
          ctx.beginPath(); ctx.arc(FERRIS.x + Math.cos(a) * FERRIS.r, fwy + Math.sin(a) * FERRIS.r, 3, 0, Math.PI * 2); ctx.fill();
        }
        const gcolors = ['#ef4444', '#f97316', '#fbbf24', '#34d399', '#38bdf8', '#a78bfa', '#f472b6', '#e2e8f0'];
        for (let i = 0; i < 8; i++) {
          const a = ang + (i * Math.PI) / 4;
          const gx2 = FERRIS.x + Math.cos(a) * FERRIS.r;
          const gy2 = fwy + Math.sin(a) * FERRIS.r;
          ctx.strokeStyle = '#4a4552'; ctx.lineWidth = 2;
          ctx.beginPath(); ctx.moveTo(gx2, gy2); ctx.lineTo(gx2, gy2 + 10); ctx.stroke();
          ctx.fillStyle = gcolors[i];
          ctx.beginPath();
          if (ctx.roundRect) ctx.roundRect(gx2 - 14, gy2 + 10, 28, 20, 6); else ctx.rect(gx2 - 14, gy2 + 10, 28, 20);
          ctx.fill();
        }
        ctx.fillStyle = '#fbbf24';
        ctx.beginPath(); ctx.arc(FERRIS.x, fwy, 10, 0, Math.PI * 2); ctx.fill();
        ctx.font = 'bold 10px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillStyle = 'rgba(255,255,255,0.75)';
        ctx.fillText(ferrisRef.current ? '🎡 enjoying the view — tap anywhere to hop off' : 'FERRIS WHEEL — tap to ride!', FERRIS.x + 30, fwy + 186);
      }
      // Turtle Shack diner
      const dy2 = DINER.doorY - cam;
      if (dy2 > -260 && dy2 < H + 120) {
        const dx2 = DINER.x;
        ctx.fillStyle = '#7a5230';
        ctx.fillRect(dx2 - 110, dy2 - 150, 220, 150);
        ctx.fillStyle = '#5a3a20';
        ctx.beginPath(); ctx.moveTo(dx2 - 124, dy2 - 150); ctx.lineTo(dx2, dy2 - 196); ctx.lineTo(dx2 + 124, dy2 - 150); ctx.closePath(); ctx.fill();
        ctx.strokeStyle = 'rgba(0,0,0,0.25)'; ctx.lineWidth = 2;
        for (let i = -5; i <= 5; i++) {
          ctx.beginPath(); ctx.moveTo(dx2 + i * 20, dy2 - 152); ctx.lineTo(dx2 + i * 16, dy2 - 176); ctx.stroke();
        }
        const wg = 0.5 + Math.abs(Math.sin(t * 1.3)) * 0.4;
        ctx.fillStyle = `rgba(253,230,138,${wg})`;
        ctx.fillRect(dx2 - 88, dy2 - 118, 40, 34); ctx.fillRect(dx2 + 48, dy2 - 118, 40, 34);
        ctx.fillStyle = '#3a2a16';
        ctx.fillRect(dx2 - 22, dy2 - 64, 44, 64);
        ctx.font = 'bold 15px monospace';
        ctx.textAlign = 'center';
        ctx.fillStyle = '#34d399';
        ctx.shadowColor = '#34d399'; ctx.shadowBlur = 12 * wg;
        ctx.fillText('🐢 TURTLE SHACK', dx2, dy2 - 158);
        ctx.shadowBlur = 0;
        ctx.font = 'bold 9px sans-serif';
        ctx.fillStyle = 'rgba(255,255,255,0.7)';
        ctx.fillText('BEACH BAR & GRILL — walk in!', dx2, dy2 + 16);
        ctx.strokeStyle = '#8a6a3a'; ctx.lineWidth = 5;
        ctx.beginPath(); ctx.moveTo(dx2 - 140, dy2); ctx.lineTo(dx2 - 146, dy2 - 52); ctx.stroke();
        ctx.fillStyle = '#2e8b57';
        [-1, 0, 1].forEach((d) => {
          ctx.beginPath(); ctx.ellipse(dx2 - 146 + d * 14, dy2 - 58, 16, 6, d * 0.5, 0, Math.PI * 2); ctx.fill();
        });
      }
      // remote players on the street (drivers render as their car, passengers ride inside)
      const liveDrivers = {};
      Object.entries(playersRef.current || {}).forEach(([sid, p]) => {
        if ((p.room || 'main') !== 'city' || !p.x) return;
        const tf = p.transform;
        if (tf && typeof tf.car === 'number' && CARS[tf.car]) { liveDrivers[tf.car] = p; return; }
        if (tf && typeof tf.in_car === 'number') return; // passenger — inside the car
        const lp = lerpPos(sid, p.x, p.y);
        drawRemotePlayer(p, lp.x, lp.y - cam, sid);
      });
      Object.entries(liveDrivers).forEach(([ciStr, p]) => {
        const ci = parseInt(ciStr, 10);
        const tf = p.transform;
        drawCar(CARS[ci], p.x, p.y, typeof tf.angle === 'number' ? tf.angle : -Math.PI / 2);
        const riders = Object.values(playersRef.current || {})
          .filter((q) => (q.room || 'main') === 'city' && q.transform && q.transform.in_car === ci)
          .map((q) => q.name)
          .filter(Boolean);
        if (passengerRef.current?.idx === ci) riders.push(user?.username || 'you');
        ctx.font = 'bold 9px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillStyle = '#fff';
        ctx.shadowColor = '#000';
        ctx.shadowBlur = 4;
        ctx.fillText(`🚗 ${p.name || 'driver'}${riders.length ? ' + ' + riders.join(', ') : ''}`, p.x, p.y - cam - 66);
        ctx.shadowBlur = 0;
        if (tf.honk && Date.now() - tf.honk < 900) drawHonkBubble(p.x, p.y - cam);
      });
      if (drivingRef.current) {
        // you're in the driver's seat — draw the car instead of the avatar
        const drv = drivingRef.current;
        drawCar(CARS[drv.idx], drv.x, drv.y, drv.ang);
        ctx.font = 'bold 9px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillStyle = '#fff';
        ctx.shadowColor = '#000';
        ctx.shadowBlur = 4;
        ctx.fillText(`🚗 ${user?.username || 'you'}`, drv.x, drv.y - cam - 66);
        ctx.shadowBlur = 0;
        if (honkRef.current && Date.now() - honkRef.current < 900) drawHonkBubble(drv.x, drv.y - cam);
      } else if (!passengerRef.current) {
        drawMyAvatar(posRef.current.x, posRef.current.y - cam);
      }
      ctx.restore(); // end world space
      // header
      const hs = H > BASE_H ? 1.6 : 1;
      ctx.fillStyle = 'rgba(0,0,0,0.55)';
      ctx.fillRect(0, 0, W, 34 * hs);
      ctx.fillStyle = '#93c5fd';
      ctx.font = `bold ${Math.round(15 * hs)}px monospace`;
      ctx.textAlign = 'center';
      ctx.shadowColor = '#93c5fd';
      ctx.shadowBlur = 8;
      ctx.fillText('🌃 RAZIENT CITY BLOCK', W / 2, 22 * hs);
      ctx.shadowBlur = 0;
      // current neighborhood + music note
      if (zoneRef.current) {
        ctx.font = `bold ${Math.round(10 * hs)}px monospace`;
        ctx.textAlign = 'left';
        ctx.fillStyle = 'rgba(253,230,138,0.9)';
        ctx.fillText(`♪ ${zoneLabel(zoneRef.current)}`, 10, 22 * hs);
        ctx.textAlign = 'center';
      }
    };

    const drawSewerScene = () => {
      drawSewer(ctx, camXRef.current, camRef.current, W, H, phaseRef.current);
      ctx.save();
      ctx.translate(-camXRef.current, -camRef.current);
      Object.entries(playersRef.current || {}).forEach(([sid, p]) => {
        if ((p.room || 'main') !== 'sewer' || !p.x) return;
        const lp = lerpPos(sid, p.x, p.y);
        drawRemotePlayer(p, lp.x, lp.y, sid);
      });
      drawMyAvatar(posRef.current.x, posRef.current.y);
      ctx.restore();
      const hs = H > BASE_H ? 1.6 : 1;
      ctx.fillStyle = 'rgba(0,0,0,0.55)';
      ctx.fillRect(0, 0, W, 34 * hs);
      ctx.font = `bold ${Math.round(13 * hs)}px monospace`;
      ctx.textAlign = 'center';
      ctx.fillStyle = '#34d399';
      ctx.shadowColor = '#34d399';
      ctx.shadowBlur = 8;
      ctx.fillText('🕳️ THE SEWERS — tap a ladder to climb out', W / 2, 22 * hs);
      ctx.shadowBlur = 0;
    };

    const imgFor = (url) => {
      let im = photoImgsRef.current.get(url);
      if (!im) {
        im = new Image();
        im.src = url;
        photoImgsRef.current.set(url, im);
      }
      return im;
    };

    const drawGalleryFrames = (photos, isOwner) => {
      FRAMES.forEach((f, i) => {
        const url = (photos || [])[i];
        ctx.fillStyle = '#0e0a14';
        ctx.fillRect(f.x - 65, f.y - 48, 130, 96);
        ctx.strokeStyle = '#b8860b';
        ctx.lineWidth = 5;
        ctx.strokeRect(f.x - 65, f.y - 48, 130, 96);
        if (url) {
          const im = imgFor(url);
          if (im.complete && im.naturalWidth > 0) {
            const fw = 120, fh = 86;
            const sc = Math.max(fw / im.naturalWidth, fh / im.naturalHeight);
            const sw = fw / sc, sh = fh / sc;
            ctx.drawImage(im, (im.naturalWidth - sw) / 2, (im.naturalHeight - sh) / 2, sw, sh, f.x - 60, f.y - 43, fw, fh);
          }
        } else {
          ctx.font = '22px sans-serif';
          ctx.textAlign = 'center';
          ctx.fillStyle = 'rgba(255,255,255,0.25)';
          ctx.fillText(isOwner ? '+' : '🖼', f.x, f.y + 8);
          if (isOwner) {
            ctx.font = '8px sans-serif';
            ctx.fillText('tap to add', f.x, f.y + 26);
          }
        }
        // spotlight
        ctx.fillStyle = 'rgba(253,230,138,0.05)';
        ctx.beginPath();
        ctx.moveTo(f.x, f.y - 70);
        ctx.lineTo(f.x - 55, f.y + 50);
        ctx.lineTo(f.x + 55, f.y + 50);
        ctx.closePath();
        ctx.fill();
      });
    };

    const drawStairsRight = (label) => {
      ctx.fillStyle = '#2c1f0c';
      for (let s = 0; s < 7; s++) {
        ctx.fillRect(644 + s * 18, 300 - s * 19, 146 - s * 18, 16);
      }
      ctx.strokeStyle = '#fbbf24';
      ctx.lineWidth = 3;
      ctx.beginPath(); ctx.moveTo(650, 292); ctx.lineTo(766, 172); ctx.stroke();
      ctx.font = 'bold 11px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillStyle = '#fde68a';
      ctx.shadowColor = '#fbbf24';
      ctx.shadowBlur = 8;
      ctx.fillText(label, 712, 158);
      ctx.shadowBlur = 0;
    };

    const drawStairsLeft = (label) => {
      ctx.fillStyle = '#2c1f0c';
      for (let s = 0; s < 7; s++) {
        ctx.fillRect(10, 300 - s * 19, 146 - s * 18, 16);
      }
      ctx.strokeStyle = '#fbbf24';
      ctx.lineWidth = 3;
      ctx.beginPath(); ctx.moveTo(150, 292); ctx.lineTo(34, 172); ctx.stroke();
      ctx.font = 'bold 11px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillStyle = '#fde68a';
      ctx.shadowColor = '#fbbf24';
      ctx.shadowBlur = 8;
      ctx.fillText(label, 88, 158);
      ctx.shadowBlur = 0;
    };

    const drawInterior = () => {
      const itr = interiorRef.current;
      if (!itr) return;
      const cfg = SUBS[itr.subRoom];
      const t = phaseRef.current;
      ctx.fillStyle = cfg.wall;
      ctx.fillRect(0, 0, W, offY + 300);
      ctx.fillStyle = cfg.floor;
      ctx.fillRect(0, offY + 300, W, H - offY - 300);
      ctx.strokeStyle = 'rgba(255,255,255,0.07)';
      ctx.lineWidth = 1;
      for (let fx = 0; fx < W + 60; fx += 50) {
        ctx.beginPath(); ctx.moveTo(fx, offY + 300); ctx.lineTo(fx - 40, H); ctx.stroke();
      }
      ctx.save();
      ctx.translate(0, offY);

      const sub = itr.subRoom;
      if (sub === 'living') {
        ctx.fillStyle = '#0a0e24';
        ctx.fillRect(300, 80, 240, 130);
        ctx.strokeStyle = '#6b5a3a'; ctx.lineWidth = 5;
        ctx.strokeRect(300, 80, 240, 130);
        for (let i = 0; i < 8; i++) {
          ctx.fillStyle = `rgba(255,255,255,${0.3 + Math.abs(Math.sin(t * 0.02 + i * 1.7)) * 0.5})`;
          ctx.fillRect(315 + (i * 31) % 210, 92 + (i * 19) % 105, 2, 2);
        }
        ctx.fillStyle = '#3a2a10'; ctx.fillRect(570, 190, 96, 110);
        ctx.fillStyle = '#0a0603'; ctx.fillRect(584, 220, 68, 72);
        const fh = 16 + Math.abs(Math.sin(t * 0.09)) * 12;
        ctx.fillStyle = 'rgba(255,120,20,0.9)';
        ctx.beginPath();
        ctx.moveTo(596, 290);
        ctx.quadraticCurveTo(618, 290 - fh, 640, 290);
        ctx.fill();
        drawStairsLeft('UPSTAIRS ▲');
      } else if (sub === 'kitchen') {
        ctx.fillStyle = '#6b5a3a'; ctx.fillRect(60, 230, 380, 70);
        ctx.fillStyle = '#8a7a55'; ctx.fillRect(60, 222, 380, 10);
        [110, 200, 290, 380].forEach((kx) => {
          ctx.fillStyle = '#4a3f28'; ctx.fillRect(kx - 30, 244, 60, 48);
          ctx.fillStyle = '#c9b98a';
          ctx.beginPath(); ctx.arc(kx, 268, 3, 0, Math.PI * 2); ctx.fill();
        });
        ctx.fillStyle = '#d8d8de'; ctx.fillRect(560, 160, 90, 140);
        ctx.fillStyle = '#b8b8c0'; ctx.fillRect(560, 220, 90, 4);
        ctx.fillStyle = '#333'; ctx.fillRect(640, 180, 4, 22);
      } else if (sub === 'bedroom') {
        ctx.fillStyle = '#b8860b'; ctx.fillRect(300, 210, 200, 16);
        ctx.fillStyle = '#4a2c0a'; ctx.fillRect(306, 226, 188, 92);
        ctx.fillStyle = '#345c8a'; ctx.fillRect(312, 232, 176, 60);
        ctx.fillStyle = '#f3e8d2'; ctx.fillRect(312, 232, 52, 26); ctx.fillRect(372, 232, 52, 26);
        // bathroom door (left)
        ctx.fillStyle = '#2a3f4a';
        ctx.fillRect(56, 165, 68, 132);
        ctx.strokeStyle = '#93c5fd'; ctx.lineWidth = 2;
        ctx.strokeRect(56, 165, 68, 132);
        ctx.fillStyle = '#93c5fd';
        ctx.beginPath(); ctx.arc(112, 232, 3, 0, Math.PI * 2); ctx.fill();
        ctx.font = 'bold 9px sans-serif'; ctx.textAlign = 'center';
        ctx.fillStyle = 'rgba(255,255,255,0.7)';
        ctx.fillText('🛁 BATH', 90, 158);
        drawStairsRight('DOWNSTAIRS ▼');
      } else if (sub === 'bathroom') {
        ctx.fillStyle = '#e8eef2';
        ctx.beginPath(); ctx.ellipse(400, 300, 120, 40, 0, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = '#7dd3fc';
        ctx.beginPath(); ctx.ellipse(400, 296, 100, 30, 0, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = '#d8dee4'; ctx.fillRect(600, 210, 80, 90);
        ctx.fillStyle = '#9fb2c0';
        ctx.beginPath(); ctx.ellipse(640, 216, 30, 10, 0, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = 'rgba(147,197,253,0.35)'; ctx.fillRect(320, 90, 160, 100);
        ctx.strokeStyle = '#93c5fd'; ctx.strokeRect(320, 90, 160, 100);
        // door back
        ctx.fillStyle = '#3a2f4a';
        ctx.fillRect(56, 165, 68, 132);
        ctx.strokeStyle = '#c4b5fd'; ctx.lineWidth = 2;
        ctx.strokeRect(56, 165, 68, 132);
        ctx.font = 'bold 9px sans-serif'; ctx.textAlign = 'center';
        ctx.fillStyle = 'rgba(255,255,255,0.7)';
        ctx.fillText('🛏 BEDROOM', 90, 158);
      } else if (sub === 'm_hall') {
        [180, 620].forEach((px) => {
          ctx.fillStyle = '#c8b98a'; ctx.fillRect(px - 14, 60, 28, 240);
          ctx.fillStyle = '#a89868'; ctx.fillRect(px - 20, 52, 40, 12); ctx.fillRect(px - 20, 296, 40, 12);
        });
        ctx.strokeStyle = '#b8860b'; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(400, 0); ctx.lineTo(400, 60); ctx.stroke();
        [-26, -13, 0, 13, 26].forEach((oxx, i) => {
          const gl = 0.5 + Math.abs(Math.sin(t * 0.05 + i)) * 0.5;
          ctx.fillStyle = `rgba(253,230,138,${gl})`;
          ctx.beginPath(); ctx.arc(400 + oxx, 74, 5, 0, Math.PI * 2); ctx.fill();
        });
        ctx.fillStyle = 'rgba(150,20,30,0.85)';
        ctx.fillRect(330, 310, 140, 170);
        drawStairsRight('UPSTAIRS ▲');
      } else if (sub === 'm_lounge') {
        ctx.fillStyle = '#3a2a10'; ctx.fillRect(90, 185, 110, 115);
        ctx.fillStyle = '#0a0603'; ctx.fillRect(106, 218, 78, 74);
        const fh3 = 18 + Math.abs(Math.sin(t * 0.09)) * 13;
        ctx.fillStyle = 'rgba(255,110,20,0.9)';
        ctx.beginPath(); ctx.moveTo(120, 290); ctx.quadraticCurveTo(145, 290 - fh3, 170, 290); ctx.fill();
        // grand piano
        ctx.fillStyle = '#0c0c10';
        ctx.beginPath();
        ctx.moveTo(320, 230); ctx.lineTo(480, 230); ctx.lineTo(500, 280); ctx.lineTo(420, 305); ctx.lineTo(320, 290);
        ctx.closePath(); ctx.fill();
        ctx.fillStyle = '#fff'; ctx.fillRect(330, 282, 90, 8);
        [336, 348, 360, 372, 384, 396, 408].forEach((kx) => {
          ctx.fillStyle = '#111'; ctx.fillRect(kx, 282, 3, 5);
        });
        drawStairsRight('GALLERY ▲');
      } else if (sub === 'm_gallery') {
        drawGalleryFrames(itr.photos, itr.isOwner);
        drawStairsRight('DOWN ▼');
      } else if (sub === 'o_lobby') {
        for (let cxx = 0; cxx < 16; cxx++) {
          for (let cyy = 0; cyy < 4; cyy++) {
            if ((cxx + cyy) % 2 === 0) {
              ctx.fillStyle = 'rgba(255,255,255,0.05)';
              ctx.fillRect(cxx * 50, 300 + cyy * 55, 50, 55);
            }
          }
        }
        [150, 650].forEach((px) => {
          ctx.fillStyle = '#c8b98a'; ctx.fillRect(px - 14, 60, 28, 240);
        });
        ctx.strokeStyle = '#b8860b'; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(400, 0); ctx.lineTo(400, 55); ctx.stroke();
        ctx.beginPath(); ctx.arc(400, 68, 22, Math.PI, 0); ctx.stroke();
        [-20, -10, 0, 10, 20].forEach((oxx, i) => {
          const gl = 0.5 + Math.abs(Math.sin(t * 0.05 + i)) * 0.5;
          ctx.fillStyle = `rgba(253,230,138,${gl})`;
          ctx.beginPath(); ctx.arc(400 + oxx, 72, 4, 0, Math.PI * 2); ctx.fill();
        });
        ctx.fillStyle = 'rgba(150,20,30,0.85)';
        ctx.fillRect(340, 300, 120, 180);
        ctx.font = 'bold 13px serif'; ctx.textAlign = 'center';
        ctx.fillStyle = '#FFD700';
        ctx.shadowColor = '#FFD700'; ctx.shadowBlur = 8;
        ctx.fillText('WELCOME TO THE ESTATE', 400, 130);
        ctx.shadowBlur = 0;
        drawStairsRight('UPSTAIRS ▲');
      } else if (sub === 'o_lounge') {
        ctx.fillStyle = '#241a10'; ctx.fillRect(70, 200, 260, 26);
        ctx.fillStyle = '#3a2c1a'; ctx.fillRect(70, 226, 260, 74);
        [100, 150, 200, 250, 300].forEach((bx, i) => {
          ctx.fillStyle = ['#7c3aed', '#e11d8f', '#22d3ee', '#fbbf24', '#34d399'][i];
          ctx.fillRect(bx - 5, 160, 10, 34);
        });
        ctx.fillStyle = '#4a1d3f'; ctx.fillRect(420, 250, 150, 55);
        ctx.fillStyle = '#5b2450'; ctx.fillRect(420, 232, 150, 20);
        ctx.fillStyle = '#3a2a10'; ctx.fillRect(620, 190, 96, 110);
        ctx.fillStyle = '#0a0603'; ctx.fillRect(634, 220, 68, 72);
        const fh4 = 16 + Math.abs(Math.sin(t * 0.09)) * 12;
        ctx.fillStyle = 'rgba(255,120,20,0.9)';
        ctx.beginPath(); ctx.moveTo(646, 290); ctx.quadraticCurveTo(668, 290 - fh4, 690, 290); ctx.fill();
        drawStairsRight('GAME ROOM ▲');
      } else if (sub === 'o_game') {
        [140, 260].forEach((ax, i) => {
          const hue = (phaseRef.current * 1.5 + i * 120) % 360;
          ctx.fillStyle = '#14101f'; ctx.fillRect(ax - 35, 160, 70, 140);
          ctx.strokeStyle = `hsl(${hue}, 90%, 60%)`; ctx.lineWidth = 2;
          ctx.strokeRect(ax - 35, 160, 70, 140);
          ctx.fillStyle = `hsla(${hue}, 90%, 60%, 0.5)`;
          ctx.fillRect(ax - 24, 176, 48, 40);
        });
        ctx.fillStyle = '#14532d'; ctx.fillRect(420, 230, 200, 90);
        ctx.strokeStyle = '#5a3a1a'; ctx.lineWidth = 6;
        ctx.strokeRect(420, 230, 200, 90);
        [[450, 255], [520, 285], [580, 250]].forEach(([bx, by], i) => {
          ctx.fillStyle = ['#fff', '#e11d48', '#fbbf24'][i];
          ctx.beginPath(); ctx.arc(bx, by, 6, 0, Math.PI * 2); ctx.fill();
        });
        const ng = 0.6 + Math.abs(Math.sin(t * 0.06)) * 0.4;
        ctx.font = 'bold 16px monospace'; ctx.textAlign = 'center';
        ctx.fillStyle = '#22d3ee';
        ctx.shadowColor = '#22d3ee'; ctx.shadowBlur = 14 * ng;
        ctx.fillText('GAME ON', 400, 110);
        ctx.shadowBlur = 0;
        drawStairsRight('GALLERY ▲');
      } else if (sub === 'o_gallery') {
        drawGalleryFrames(itr.photos, itr.isOwner);
        drawStairsRight('ROOFTOP ▲');
      } else if (sub === 'o_roof') {
        for (let i = 0; i < 24; i++) {
          ctx.fillStyle = `rgba(255,255,255,${0.3 + Math.abs(Math.sin(t * 0.02 + i * 1.9)) * 0.6})`;
          ctx.fillRect((i * 67) % W, 20 + (i * 23) % 150, 2, 2);
        }
        ctx.fillStyle = '#141a38';
        [[40, 60, 44], [120, 44, 36], [660, 54, 40], [730, 66, 44]].forEach(([bx, bh, bw]) => {
          ctx.fillRect(bx, 210 - bh, bw, bh);
        });
        ctx.strokeStyle = '#b8860b'; ctx.lineWidth = 3;
        ctx.beginPath(); ctx.moveTo(0, 212); ctx.lineTo(W, 212); ctx.stroke();
        for (let rx = 16; rx < W; rx += 34) {
          ctx.beginPath(); ctx.moveTo(rx, 212); ctx.lineTo(rx, 246); ctx.stroke();
        }
        // rooftop pool
        ctx.fillStyle = '#d9cdb8';
        ctx.beginPath(); ctx.ellipse(250, 400, 130, 42, 0, 0, Math.PI * 2); ctx.fill();
        ctx.strokeStyle = '#b8860b'; ctx.lineWidth = 4;
        ctx.beginPath(); ctx.ellipse(250, 400, 130, 42, 0, 0, Math.PI * 2); ctx.stroke();
        const wg = ctx.createLinearGradient(130, 370, 380, 440);
        wg.addColorStop(0, '#38bdf8'); wg.addColorStop(1, '#0369a1');
        ctx.fillStyle = wg;
        ctx.beginPath(); ctx.ellipse(250, 397, 112, 32, 0, 0, Math.PI * 2); ctx.fill();
        // telescope
        ctx.strokeStyle = '#9ca3af'; ctx.lineWidth = 5;
        ctx.beginPath(); ctx.moveTo(600, 300); ctx.lineTo(640, 250); ctx.stroke();
        ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(600, 300); ctx.lineTo(585, 330); ctx.moveTo(600, 300); ctx.lineTo(615, 330); ctx.stroke();
        ctx.font = 'bold 12px serif'; ctx.textAlign = 'center';
        ctx.fillStyle = '#FFD700';
        ctx.shadowColor = '#FFD700'; ctx.shadowBlur = 10;
        ctx.fillText('✦ TOP OF THE WORLD ✦', 400, 60);
        ctx.shadowBlur = 0;
        drawStairsRight('DOWN ▼');
      }

      if (sub === 'diner') drawDinerScene(t);
      if (SHOPS[sub]) drawShopScene(ctx, sub, t);
      if (sub.startsWith('g_')) drawGodsRoom(ctx, sub, phaseRef.current);
      if (sub === 'moon_surface' || sub.startsWith('sp_') || sub === 'mr_base') drawMoonRoom(ctx, sub, phaseRef.current);
      if (sub.startsWith('sp_') || sub === 'mr_base') drawSpaceFrames(ctx, sub, spacePhotosRef.current, imgFor, isStaff || (sub === 'mr_base' && isRandall), phaseRef.current);
      if (PLANET_KIND_TO_ID[itr.kind]) drawPlanetRoom(ctx, sub, phaseRef.current, { rental: spaceHousesRef.current[PLANET_KIND_TO_ID[itr.kind]] });

      // exit door mat (entry rooms) — walk onto it to leave
      const planetInner = PLANET_KIND_TO_ID[itr.kind] && sub !== CHAINS[itr.kind][0];
      if ((sub === CHAINS[itr.kind][0] && itr.kind !== 'moon' && !PLANET_KIND_TO_ID[itr.kind]) || sub === 'g_lobby' || sub === 'sp_lobby' || planetInner) {
        const toMoon = itr.kind === 'space_mansion' || itr.kind === 'moon_randall';
        const eg = 0.5 + Math.abs(Math.sin(t * 0.045)) * 0.5;
        ctx.fillStyle = 'rgba(6,38,26,0.88)';
        ctx.fillRect(336, 448, 128, 38);
        ctx.strokeStyle = `rgba(52,211,153,${eg})`;
        ctx.lineWidth = 2.5;
        ctx.strokeRect(336, 448, 128, 38);
        ctx.font = 'bold 12px monospace';
        ctx.textAlign = 'center';
        ctx.fillStyle = `rgba(110,231,183,${0.55 + eg * 0.45})`;
        ctx.shadowColor = '#34d399';
        ctx.shadowBlur = 9 * eg;
        ctx.fillText('▼ EXIT ▼', 400, 464);
        ctx.shadowBlur = 0;
        ctx.font = '9px sans-serif';
        ctx.fillStyle = 'rgba(255,255,255,0.55)';
        ctx.fillText(planetInner ? 'to surface' : toMoon ? 'to the moon' : 'to street', 400, 478);
      }

      // placed furniture
      const placed = (itr.furniture || [])
        .filter((f) => f.sub_room === itr.subRoom)
        .sort((a, b) => a.y - b.y);
      placed.forEach((f) => {
        drawFurniture(ctx, f.item_id, f.x, f.y, phaseRef.current, editRef.current.editMode);
      });

      // players (depth sorted)
      const roomId = `house_${itr.houseNumber}_${itr.subRoom}`;
      const drawables = Object.entries(playersRef.current || {})
        .filter(([, p]) => (p.room || 'main') === roomId && p.x)
        .map(([sid, p]) => {
          const lp = lerpPos(sid, p.x, p.y);
          return { y: lp.y, fn: () => drawRemotePlayer(p, lp.x, lp.y, sid) };
        });
      drawables.push({ y: posRef.current.y, fn: () => drawMyAvatar(posRef.current.x, posRef.current.y) });
      drawables.sort((a, b) => a.y - b.y);
      drawables.forEach((d) => d.fn());
      ctx.restore();

      // header
      const hs2 = H > BASE_H ? 1.6 : 1;
      const kindLabel = itr.kind === 'owner_mansion' || itr.kind === 'diner' || itr.kind === 'gods_mansion' || itr.kind === 'moon' || itr.kind === 'space_mansion' || itr.kind === 'moon_randall' || PLANET_KIND_TO_ID[itr.kind] || SHOPS[itr.kind]
        ? ''
        : itr.kind === 'mansion'
          ? `${MANSION_TITLES[itr.houseNumber] || 'MANSION'} — `
          : `HOUSE ${String(itr.houseNumber).padStart(2, '0')} — `;
      ctx.fillStyle = 'rgba(0,0,0,0.55)';
      ctx.fillRect(0, 0, W, 40 * hs2);
      ctx.font = `bold ${Math.round(14 * hs2)}px monospace`;
      ctx.textAlign = 'center';
      ctx.fillStyle = '#fff';
      ctx.fillText(`${kindLabel}${cfg.name.toUpperCase()}`, W / 2, 17 * hs2);
      ctx.font = `bold ${Math.round(11 * hs2)}px serif`;
      ctx.fillStyle = '#FFD700';
      ctx.shadowColor = '#FFD700';
      ctx.shadowBlur = 6;
      ctx.fillText(`★ ${(itr.ownerName || '').toUpperCase()} ★`, W / 2, 33 * hs2);
      ctx.shadowBlur = 0;
      if (editRef.current.editMode) {
        ctx.fillStyle = 'rgba(251,191,36,0.9)';
        ctx.font = `bold ${Math.round(11 * hs2)}px sans-serif`;
        ctx.fillText(
          editRef.current.placingItem ? 'Tap anywhere to place your item' : 'Edit mode: tap an item to pick it up, or select furniture below',
          W / 2, H - 10
        );
      }
    };

    const drawSpaceScene = () => {
      const tgt = targetRef.current;
      if (tgt) {
        const dxs = tgt.x - posRef.current.x, dys = tgt.y - posRef.current.y;
        if (Math.hypot(dxs, dys) > 6) shipAngRef.current = Math.atan2(dys, dxs);
      }
      const others = Object.entries(playersRef.current || {})
        .filter(([, p]) => (p.room || 'main') === 'space' && p.x != null)
        .map(([, p]) => ({ x: p.x, y: p.y, ang: -Math.PI / 2, walking: p.is_walking }));
      drawSpaceWorld(ctx, camXRef.current, camRef.current, phaseRef.current, {
        x: posRef.current.x, y: posRef.current.y, ang: shipAngRef.current, thrusting: walkingRef.current,
      }, others);
      const hs3 = H > BASE_H ? 1.6 : 1;
      ctx.fillStyle = 'rgba(0,0,0,0.55)';
      ctx.fillRect(0, 0, W, 34 * hs3);
      ctx.font = `bold ${Math.round(13 * hs3)}px monospace`;
      ctx.textAlign = 'center';
      ctx.fillStyle = '#c084fc';
      ctx.fillText('🛸 DEEP SPACE — tap anywhere to cruise', W / 2, 22 * hs3);
    };

    const loop = () => {
      phaseRef.current += 1;
      // driving a car — smooth acceleration, real turning, honking
      if (drivingRef.current && modeRef.current === 'street') {
        const drv = drivingRef.current;
        const dv = driveVecRef.current;
        if (keysRef.current.honkNow) { keysRef.current.honkNow = false; honk(); }
        const ACC = 0.5;
        const MAXS = drv.limo ? 6.2 : 7.4;
        if (dv.x || dv.y) {
          drv.vx += dv.x * ACC;
          drv.vy += dv.y * ACC;
        } else {
          drv.vx *= 0.9;
          drv.vy *= 0.9;
          if (Math.abs(drv.vx) < 0.05) drv.vx = 0;
          if (Math.abs(drv.vy) < 0.05) drv.vy = 0;
        }
        const spd = Math.hypot(drv.vx, drv.vy);
        if (spd > MAXS) { drv.vx *= MAXS / spd; drv.vy *= MAXS / spd; }
        if (spd > 0.5) {
          const ta = Math.atan2(drv.vy, drv.vx);
          let dAng = ta - drv.ang;
          while (dAng > Math.PI) dAng -= Math.PI * 2;
          while (dAng < -Math.PI) dAng += Math.PI * 2;
          drv.ang += dAng * 0.16;
        }
        const cl = clampToRects(drv.x + drv.vx, drv.y + drv.vy, drv.x, drv.y, DRIVE_RECTS);
        if (cl.x === drv.x && drv.vx) drv.vx *= 0.3;
        if (cl.y === drv.y && drv.vy) drv.vy *= 0.3;
        drv.x = cl.x;
        drv.y = cl.y;
        posRef.current = { x: drv.x, y: drv.y };
        targetRef.current = null;
        walkingRef.current = false;
        const nowD = Date.now();
        if (nowD - lastSendRef.current > 80 && socketRef?.current?.connected) {
          lastSendRef.current = nowD;
          socketRef.current.emit('player_move', {
            x: Math.round(drv.x), y: Math.round(drv.y), is_walking: spd > 0.5,
            direction: Math.abs(drv.vy) >= Math.abs(drv.vx) ? (drv.vy < 0 ? 'up' : 'down') : (drv.vx < 0 ? 'left' : 'right'),
            transform: { car: drv.idx, angle: Math.round(drv.ang * 100) / 100, honk: honkRef.current },
          });
        }
      }
      // riding shotgun — glued to the driver's car
      if (passengerRef.current && modeRef.current === 'street') {
        const pas = passengerRef.current;
        const driver = Object.values(playersRef.current || {}).find(
          (p) => (p.room || 'main') === 'city' && p.transform && p.transform.car === pas.idx
        );
        if (driver) {
          pas.lastSeen = Date.now();
          posRef.current = { x: driver.x, y: driver.y };
          targetRef.current = null;
          walkingRef.current = false;
          const nowP = Date.now();
          if (nowP - lastSendRef.current > 100 && socketRef?.current?.connected) {
            lastSendRef.current = nowP;
            socketRef.current.emit('player_move', {
              x: Math.round(driver.x), y: Math.round(driver.y), is_walking: false, direction: 'down',
              transform: { in_car: pas.idx },
            });
          }
        } else if (Date.now() - (pas.lastSeen || 0) > 2000) {
          hopOut(true);
          toast.info('The driver parked — you hopped out');
        }
      }
      // Ferris wheel ride — your seat follows the gondola around
      if (ferrisRef.current && modeRef.current === 'street') {
        const a = ferrisAngle();
        posRef.current = { x: FERRIS.x + Math.cos(a) * FERRIS.r + (ferrisRef.current.seat || 0), y: FERRIS.y + Math.sin(a) * FERRIS.r + 24 };
        targetRef.current = null;
        walkingRef.current = false;
        const nowR = Date.now();
        if (nowR - lastSendRef.current > 120 && socketRef?.current?.connected) {
          lastSendRef.current = nowR;
          socketRef.current.emit('player_move', {
            x: Math.round(posRef.current.x), y: Math.round(posRef.current.y), is_walking: false, direction: 'down',
          });
        }
      }
      const target = targetRef.current;
      if (target) {
        const dx = target.x - posRef.current.x;
        const dy = target.y - posRef.current.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist > 3) {
          const sp = modeRef.current === 'space'
            ? 7.5
            : (modeRef.current === 'street' && inWater(posRef.current.x, posRef.current.y))
              ? (jetskiRef.current ? 5.4 : 1.5) : 2.6;
          let nxT = posRef.current.x + (dx / dist) * sp;
          let nyT = posRef.current.y + (dy / dist) * sp;
          if (modeRef.current === 'street' || modeRef.current === 'sewer') {
            const rcts = modeRef.current === 'sewer' ? SEWER_RECTS : WALK_RECTS;
            const blks = modeRef.current === 'sewer' ? null : HOUSE_BLOCKS;
            const cl2 = clampToRects(nxT, nyT, posRef.current.x, posRef.current.y, rcts, blks);
            if (cl2.x === posRef.current.x && cl2.y === posRef.current.y) targetRef.current = null;
            nxT = cl2.x;
            nyT = cl2.y;
          }
          posRef.current = { x: nxT, y: nyT };
          const dir = directionFromDelta(dx, dy);
          if (dir) dirRef.current = dir;
          walkingRef.current = true;
          const now = Date.now();
          if (now - lastSendRef.current > 50 && socketRef?.current?.connected) {
            lastSendRef.current = now;
            seqRef.current++;
            socketRef.current.emit('player_move', {
              seq: seqRef.current,
              x: Math.round(posRef.current.x),
              y: Math.round(posRef.current.y),
              is_walking: true,
              direction: dirRef.current,
            });
          }
        } else {
          targetRef.current = null;
          walkingRef.current = false;
          socketRef?.current?.emit('player_move', {
            seq: ++seqRef.current,
            x: Math.round(posRef.current.x),
            y: Math.round(posRef.current.y),
            is_walking: false,
            direction: dirRef.current,
          });
        }
      } else {
        walkingRef.current = false;
      }
      // keyboard (WASD/arrows) — takes precedence over click-to-move
      {
        const keys = keysRef.current;
        const kx = (keys.right ? 1 : 0) - (keys.left ? 1 : 0);
        const ky = (keys.down ? 1 : 0) - (keys.up ? 1 : 0);
        if (drivingRef.current && modeRef.current === 'street') {
          if (kx || ky) {
            const n = Math.hypot(kx, ky);
            driveVecRef.current = { x: kx / n, y: ky / n };
            keyDriveRef.current = true;
          } else if (keyDriveRef.current) {
            keyDriveRef.current = false;
            driveVecRef.current = { x: 0, y: 0 };
          }
        } else if ((kx || ky) && !ferrisRef.current) {
          if (dinerRef.current?.seat != null) { dinerRef.current = null; setShowMenu(false); }
          targetRef.current = null;
          const n = Math.hypot(kx, ky);
          const sp = modeRef.current === 'space'
            ? 7.5
            : (modeRef.current === 'street' && inWater(posRef.current.x, posRef.current.y))
              ? (jetskiRef.current ? 5.4 : 1.5) : 2.6;
          let nx2 = posRef.current.x + (kx / n) * sp;
          let ny2 = posRef.current.y + (ky / n) * sp;
          if (modeRef.current === 'street' || modeRef.current === 'sewer') {
            const rcts = modeRef.current === 'sewer' ? SEWER_RECTS : WALK_RECTS;
            const blks = modeRef.current === 'sewer' ? null : HOUSE_BLOCKS;
            const cl3 = clampToRects(nx2, ny2, posRef.current.x, posRef.current.y, rcts, blks);
            nx2 = cl3.x;
            ny2 = cl3.y;
          } else if (modeRef.current === 'space') {
            nx2 = Math.max(30, Math.min(SPACE_W - 30, nx2));
            ny2 = Math.max(30, Math.min(SPACE_H - 30, ny2));
            shipAngRef.current = Math.atan2(ky, kx);
          } else {
            nx2 = Math.max(70, Math.min(730, nx2));
            ny2 = Math.max(300, Math.min(480, ny2));
          }
          posRef.current = { x: nx2, y: ny2 };
          const kdir = directionFromDelta(kx, ky);
          if (kdir) dirRef.current = kdir;
          walkingRef.current = true;
          keyWalkRef.current = true;
          const nowK = Date.now();
          if (nowK - lastSendRef.current > 50 && socketRef?.current?.connected) {
            lastSendRef.current = nowK;
            socketRef.current.emit('player_move', {
              seq: ++seqRef.current, x: Math.round(nx2), y: Math.round(ny2), is_walking: true, direction: dirRef.current,
            });
          }
        } else if (keyWalkRef.current) {
          keyWalkRef.current = false;
          walkingRef.current = false;
          socketRef?.current?.emit('player_move', {
            seq: ++seqRef.current, x: Math.round(posRef.current.x), y: Math.round(posRef.current.y), is_walking: false, direction: dirRef.current,
          });
        }
      }
      // stepping onto the exit mat walks you out the front door
      if (modeRef.current === 'interior') {
        const eItr = interiorRef.current;
        if (eItr && eItr.kind !== 'moon' && Math.abs(posRef.current.x - 400) < 55 && posRef.current.y > 448) {
          const pid2 = PLANET_KIND_TO_ID[eItr.kind];
          if (pid2) {
            if (eItr.subRoom !== `${pid2}_surface`) {
              setInterior({ ...eItr, subRoom: `${pid2}_surface` });
              posRef.current = { x: 400, y: 430 };
              targetRef.current = null;
              emitRoom(`house_${eItr.houseNumber}_${pid2}_surface`);
            }
          } else {
            const onExitRoom = eItr.subRoom === CHAINS[eItr.kind][0] || eItr.subRoom === 'g_lobby' || eItr.subRoom === 'sp_lobby';
            if (onExitRoom) {
              if (eItr.kind === 'space_mansion' || eItr.kind === 'moon_randall') enterMoonSurface();
              else exitToStreet();
            }
          }
        }
      }
      if (modeRef.current === 'street') {
        const camSpeed = drivingRef.current || passengerRef.current ? 0.18 : 0.12;
        const targetCam = Math.max(0, Math.min(WORLD_H - H, posRef.current.y - H * 0.45));
        camRef.current += (targetCam - camRef.current) * camSpeed;
        const targetCamX = Math.max(CAM_MIN_X, Math.min(CAM_MAX_X, posRef.current.x - 400));
        camXRef.current += (targetCamX - camXRef.current) * camSpeed;
        const z = zoneForY(posRef.current.y);
        if (z !== zoneRef.current) {
          zoneRef.current = z;
          musicRef.current?.setZone(z);
        }
      } else if (modeRef.current === 'space') {
        const tcy = Math.max(0, Math.min(Math.max(0, SPACE_H - H), posRef.current.y - H / 2));
        const tcx = Math.max(0, Math.min(Math.max(0, SPACE_W - W), posRef.current.x - W / 2));
        camRef.current += (tcy - camRef.current) * 0.12;
        camXRef.current += (tcx - camXRef.current) * 0.12;
        if (Math.hypot(posRef.current.x - SPACE_MOON.x, posRef.current.y - SPACE_MOON.y) < SPACE_MOON.r + 36) {
          targetRef.current = null;
          enterMoonSurface();
          toast.success('🌕 Touchdown!');
        }
        for (const p of PLANETS) {
          if (Math.hypot(posRef.current.x - p.x, posRef.current.y - p.y) < p.r + 30) {
            targetRef.current = null;
            enterPlanet(p.id);
            break;
          }
        }
        if (zoneRef.current !== null) {
          zoneRef.current = null;
          musicRef.current?.setZone(null);
        }
      } else if (modeRef.current === 'sewer') {
        const tcy = Math.max(0, Math.min(Math.max(0, SEWER_H - H), posRef.current.y - H * 0.5));
        const tcx = Math.max(0, Math.min(Math.max(0, SEWER_W - W), posRef.current.x - W / 2));
        camRef.current += (tcy - camRef.current) * 0.14;
        camXRef.current += (tcx - camXRef.current) * 0.14;
        if (zoneRef.current !== null) {
          zoneRef.current = null;
          musicRef.current?.setZone(null);
        }
      } else if (zoneRef.current !== null) {
        zoneRef.current = null;
        musicRef.current?.setZone(null);
      }
      ctx.clearRect(0, 0, W, H);
      if (modeRef.current === 'street') drawStreet();
      else if (modeRef.current === 'sewer') drawSewerScene();
      else if (modeRef.current === 'space') drawSpaceScene();
      else drawInterior();
      raf = requestAnimationFrame(loop);
    };
    loop();
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line
  }, [houses, ownerMansion, avatar, user]);

  const placedIds = (interior?.furniture || []).map((f) => f.item_id);
  const inventory = ownedItems.filter((id) => !placedIds.includes(id));
  const daysLeft = interior?.expiresAt
    ? Math.max(0, Math.ceil((new Date(interior.expiresAt) - Date.now()) / 86400000))
    : null;
  const rentInfo = rentPrompt >= MANSION_NUM
    ? { label: rentPrompt === MANSION_NUM ? 'the Grand Mansion' : `Elite Mansion ${rentPrompt === 18 ? 'I' : 'II'}`, price: 1000 }
    : { label: `House ${String(rentPrompt || 0).padStart(2, '0')}`, price: 250 };
  const chain = interior ? CHAINS[interior.kind] : [];
  const ci = interior ? chain.indexOf(interior.subRoom) : -1;

  return (
    <div className="relative mx-auto" style={{ aspectRatio: `${W} / ${H}`, width: `min(100%, calc((100vh - 70px) * ${(W / H).toFixed(4)}))` }} data-testid="city-block">
      <canvas
        ref={canvasRef}
        data-testid="city-canvas"
        width={W}
        height={H}
        onClick={(e) => { if (Date.now() - lastTouchRef.current < 700) return; handleClick(e); }}
        onTouchStart={(e) => { lastTouchRef.current = Date.now(); handleClick(e); }}
        className="w-full h-full cursor-pointer touch-none"
        data-testid="city-canvas"
      />
      <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoFile} data-testid="photo-input" />

      {/* Top-right controls */}
      <div className="absolute top-2 right-2 flex items-center gap-2">
        <span className="bg-black/60 text-yellow-400 text-xs px-2 py-1 rounded-full flex items-center gap-1" data-testid="city-vibecoins">
          <Coins className="w-3 h-3" /> {vibecoins}
        </span>
        <Button
          size="sm" variant="ghost" className="bg-black/60 text-white h-7 w-7 p-0"
          onClick={() => {
            const next = !musicOn;
            setMusicOn(next);
            musicRef.current?.setEnabled(next);
          }}
          data-testid="city-music-toggle"
        >
          {musicOn ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5 text-white/50" />}
        </Button>
        {mode === 'street' ? (
          <Button size="sm" variant="ghost" className="bg-black/60 text-white text-xs h-7" onClick={() => onExitClub?.()} data-testid="city-exit-btn">
            <LogOut className="w-3 h-3 mr-1" /> Club
          </Button>
        ) : mode === 'space' ? (
          <Button size="sm" variant="ghost" className="bg-black/60 text-emerald-300 text-xs h-7" onClick={() => enterMoonSurface()} data-testid="space-land-btn">
            🌕 Land
          </Button>
        ) : (
          <Button size="sm" variant="ghost" className="bg-black/60 text-white text-xs h-7" onClick={exitToStreet} data-testid="house-leave-btn">
            <DoorOpen className="w-3 h-3 mr-1" /> Street
          </Button>
        )}
      </div>

      {/* Interior controls */}
      {mode === 'interior' && interior && (
        <>
          <div className="absolute inset-y-0 left-1 flex items-center pointer-events-none [&_button]:pointer-events-auto">
            {interior.subRoom === 'kitchen' && (
              <Button size="sm" className="bg-black/70 hover:bg-black/90 text-white h-9" onClick={() => goSub('living')} data-testid="house-to-living">
                <ArrowLeft className="w-4 h-4 mr-1" /> Living Room
              </Button>
            )}
            {interior.kind !== 'house' && ci > 0 && (
              <Button size="sm" className="bg-black/70 hover:bg-black/90 text-yellow-300 h-9" onClick={() => tryFloor(-1)} data-testid="floor-down-btn">
                <ArrowDown className="w-4 h-4 mr-1" /> Down
              </Button>
            )}
          </div>
          <div className="absolute inset-y-0 right-1 flex items-center gap-1 flex-col justify-center pointer-events-none [&_button]:pointer-events-auto">
            {interior.subRoom === 'living' && (
              <>
                <Button size="sm" className="bg-black/70 hover:bg-black/90 text-white h-9" onClick={() => goSub('kitchen')} data-testid="house-to-kitchen">
                  Kitchen <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
                <Button size="sm" className="bg-black/70 hover:bg-black/90 text-yellow-300 h-9" onClick={() => goSub('bedroom')} data-testid="house-upstairs-btn">
                  Upstairs <ArrowUp className="w-4 h-4 ml-1" />
                </Button>
              </>
            )}
            {interior.subRoom === 'bedroom' && (
              <Button size="sm" className="bg-black/70 hover:bg-black/90 text-yellow-300 h-9" onClick={() => goSub('living')} data-testid="house-downstairs-btn">
                Down <ArrowDown className="w-4 h-4 ml-1" />
              </Button>
            )}
            {interior.subRoom === 'bathroom' && (
              <Button size="sm" className="bg-black/70 hover:bg-black/90 text-white h-9" onClick={() => goSub('bedroom')} data-testid="house-to-bedroom">
                Bedroom <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            )}
            {interior.kind !== 'house' && ci >= 0 && ci < chain.length - 1 && (
              <Button size="sm" className="bg-black/70 hover:bg-black/90 text-yellow-300 h-9" onClick={() => tryFloor(1)} data-testid="floor-up-btn">
                Up <ArrowUp className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>

          {/* Randall's Sky Club guest list */}
          {interior.kind === 'gods_mansion' && (isStaff || isRandall) && (
            <div className="absolute bottom-2 left-2">
              <Button size="sm" className="h-8 text-xs bg-black/70 text-yellow-300 border border-yellow-500/40" onClick={() => { fetchSkyAccess(); setGuestModal(true); }} data-testid="skyclub-guestlist-btn">
                ☁️ Sky Club Guest List
              </Button>
            </div>
          )}

          {/* Sky Club YouTube cue — plays for everyone in the club */}
          {interior.subRoom === 'g_club' && (
            <SkyClubCue authFetch={authFetch} API_URL={API_URL} canManage={isStaff || isRandall} />
          )}

          {/* Owner tools */}
          {interior.isOwner && (
            <div className="absolute bottom-2 left-2 flex items-center gap-2 flex-wrap">
              <Button size="sm" className={`h-8 text-xs ${editMode ? 'bg-yellow-500 text-black' : 'bg-black/70 text-white'}`} onClick={() => { setEditMode(!editMode); setPlacingItem(null); }} data-testid="house-edit-btn">
                <Pencil className="w-3 h-3 mr-1" /> {editMode ? 'Done' : 'Edit Room'}
              </Button>
              <Button size="sm" className="h-8 text-xs bg-black/70 text-white" onClick={() => setShowShop(true)} data-testid="house-furniture-shop-btn">
                <Sofa className="w-3 h-3 mr-1" /> Furniture Shop
              </Button>
              {interior.kind !== 'owner_mansion' && interior.kind !== 'space_mansion' && (
                <Button size="sm" className="h-8 text-xs bg-black/70 text-emerald-300" onClick={handleRenew} data-testid="house-renew-btn">
                  <RefreshCw className="w-3 h-3 mr-1" /> {ADMIN_NUMS.includes(interior.houseNumber) ? 'Extend Free 🛡️' : `Renew (${interior.kind === 'mansion' ? 1000 : 250})`}
                </Button>
              )}
              {(interior.subRoom === 'm_gallery' || interior.subRoom === 'o_gallery') && (
                <Button size="sm" className="h-8 text-xs bg-black/70 text-pink-300" onClick={() => fileInputRef.current?.click()} data-testid="add-photo-btn">
                  <Camera className="w-3 h-3 mr-1" /> Frame a Photo
                </Button>
              )}
              {daysLeft !== null && interior.kind !== 'owner_mansion' && (
                <span className={`text-xs px-2 py-1 rounded-full ${daysLeft <= 1 ? 'bg-red-500/80 text-white' : 'bg-black/60 text-white/80'}`} data-testid="house-days-left">
                  {daysLeft}d left
                </span>
              )}
            </div>
          )}

          {/* Inventory bar (edit mode) */}
          {editMode && interior.isOwner && (
            <div className="absolute bottom-12 left-2 right-2 bg-black/80 rounded-lg p-2 flex gap-2 overflow-x-auto" data-testid="house-inventory-bar">
              {inventory.length === 0 ? (
                <span className="text-white/50 text-xs py-1">No unplaced furniture — visit the Furniture Shop!</span>
              ) : inventory.map((id) => {
                const item = FURNITURE.find((f) => f.id === id);
                return (
                  <button
                    key={id}
                    className={`shrink-0 px-2 py-1 rounded text-xs border ${placingItem === id ? 'border-yellow-400 bg-yellow-400/20 text-yellow-200' : 'border-white/20 text-white/80 hover:border-white/50'}`}
                    onClick={() => setPlacingItem(placingItem === id ? null : id)}
                    data-testid={`hinv-${id}`}
                  >
                    {item?.name || id}
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Planet house rent modal */}
      {rentPlanet && (
        <div className="absolute inset-0 bg-black/70 flex items-center justify-center z-20" onClick={(e) => e.target === e.currentTarget && setRentPlanet(null)}>
          <div className="bg-gray-900 border border-cyan-500/60 rounded-xl p-5 w-full max-w-xs text-center" data-testid="planet-rent-modal">
            <p className="text-3xl mb-2">{{ mars: '🔴', venus: '☁️', saturn: '🪐' }[rentPlanet]}</p>
            <h3 className="text-white font-bold mb-1">{PLANET_HOUSE_NAMES[rentPlanet]}</h3>
            <p className="text-white/60 text-sm mb-3">Rent this space house for <span className="text-yellow-300 font-bold">2,500 VibeCoins</span> · 7 days. It's all yours — nobody else gets in.</p>
            <div className="flex gap-2 justify-center">
              <Button size="sm" className="bg-cyan-600 hover:bg-cyan-500" onClick={confirmRentPlanet} data-testid="planet-rent-confirm-btn">Rent it 🏠</Button>
              <Button size="sm" variant="ghost" className="bg-white/10 text-white" onClick={() => setRentPlanet(null)} data-testid="planet-rent-cancel-btn">Not now</Button>
            </div>
          </div>
        </div>
      )}

      {/* Sky Club guest list modal */}
      {guestModal && (
        <div className="absolute inset-0 bg-black/70 flex items-center justify-center z-20" onClick={(e) => e.target === e.currentTarget && setGuestModal(false)}>
          <div className="bg-gray-900 border border-yellow-500/60 rounded-xl p-5 w-full max-w-xs" data-testid="skyclub-guestlist-modal">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-white font-bold">☁️ Sky Club Guest List</h3>
              <button onClick={() => setGuestModal(false)}><X className="w-4 h-4 text-white/60" /></button>
            </div>
            <p className="text-white/50 text-xs mb-3">Only these usernames (or emails) can ride up to the Sky Club. Randall & staff always get in.</p>
            <div className="flex gap-2 mb-3">
              <Input
                placeholder="username or email"
                value={guestName}
                onChange={(e) => setGuestName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addGuest()}
                className="h-8 text-xs"
                data-testid="skyclub-guest-input"
              />
              <Button size="sm" className="h-8 bg-yellow-500 hover:bg-yellow-400 text-black font-bold" onClick={addGuest} data-testid="skyclub-guest-add-btn">Add</Button>
            </div>
            <div className="max-h-44 overflow-y-auto space-y-1" data-testid="skyclub-guest-items">
              {guestList.length === 0 ? (
                <p className="text-white/40 text-xs text-center py-3">No guests yet — the club's all yours 👑</p>
              ) : guestList.map((g) => (
                <div key={g} className="flex items-center justify-between bg-white/5 rounded px-2 py-1.5">
                  <span className="text-white/80 text-xs truncate">{g}</span>
                  <button onClick={() => removeGuest(g)} className="text-red-400/70 hover:text-red-300" data-testid={`skyclub-guest-remove-${g}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Rent modal */}
      {rentPrompt && (
        <div className="absolute inset-0 bg-black/70 flex items-center justify-center z-20" onClick={(e) => e.target === e.currentTarget && setRentPrompt(null)}>
          <div className="bg-gray-900 border border-blue-400/50 rounded-xl p-5 max-w-xs text-center" data-testid="house-rent-modal">
            <DoorOpen className="w-8 h-8 text-blue-300 mx-auto mb-2" />
            <h3 className="text-white font-bold text-lg">Rent {rentInfo.label}?</h3>
            <p className="text-white/60 text-sm mt-1">
              {rentInfo.price} VibeCoins for 7 days. {rentPrompt >= MANSION_NUM ? 'Three grand floors of luxury.' : 'Two floors — twice the space of an apartment.'} Renew before it expires or it goes back on the market!
            </p>
            <p className="text-yellow-400 text-xs mt-2 flex items-center justify-center gap-1">
              <Coins className="w-3 h-3" /> Your balance: {vibecoins}
            </p>
            <div className="flex gap-2 mt-4 justify-center">
              <Button size="sm" variant="outline" onClick={() => setRentPrompt(null)} data-testid="house-rent-cancel-btn">Not now</Button>
              <Button size="sm" className="bg-blue-600 hover:bg-blue-500 text-white font-bold" onClick={() => handleRent(rentPrompt)} data-testid="house-rent-confirm-btn">
                Rent ({rentInfo.price})
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Owner mansion claim modal */}
      {claimPrompt && (
        <div className="absolute inset-0 bg-black/70 flex items-center justify-center z-20" onClick={(e) => e.target === e.currentTarget && setClaimPrompt(false)}>
          <div className="bg-gray-900 border border-yellow-500/60 rounded-xl p-5 max-w-xs text-center" data-testid="mansion-claim-modal">
            <Crown className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
            <h3 className="text-white font-bold text-lg">Claim the Razient Estate?</h3>
            <p className="text-white/60 text-sm mt-1">
              Five stories: lobby, velvet lounge, game room, royal gallery with photo frames, and a rooftop suite. Free for the boss — yours forever.
            </p>
            <div className="flex gap-2 mt-4 justify-center">
              <Button size="sm" variant="outline" onClick={() => setClaimPrompt(false)} data-testid="mansion-claim-cancel-btn">Not yet</Button>
              <Button size="sm" className="bg-yellow-500 hover:bg-yellow-400 text-black font-bold" onClick={handleClaim} data-testid="mansion-claim-btn">
                <Crown className="w-3 h-3 mr-1" /> Claim it
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Driving controls: joystick + honk + park */}
      {driving && (
        <>
          <div className="absolute bottom-8 left-6 z-30" data-testid="drive-joystick">
            <MobileJoystick
              onMove={(nx, ny) => { driveVecRef.current = { x: nx, y: ny }; }}
              onRelease={() => { driveVecRef.current = { x: 0, y: 0 }; }}
            />
          </div>
          <Button
            size="sm"
            className="absolute bottom-24 right-6 z-30 bg-amber-500 hover:bg-amber-400 text-black font-bold"
            onClick={honk}
            data-testid="honk-btn"
          >
            📣 Honk
          </Button>
          <Button
            size="sm"
            className="absolute bottom-12 right-6 z-30 bg-red-600 hover:bg-red-500 text-white font-bold"
            onClick={parkCar}
            data-testid="park-car-btn"
          >
            🅿️ Park & Get Out
          </Button>
        </>
      )}

      {/* Riding shotgun */}
      {riding && (
        <Button
          size="sm"
          className="absolute bottom-12 right-6 z-30 bg-red-600 hover:bg-red-500 text-white font-bold"
          onClick={() => hopOut()}
          data-testid="hop-out-btn"
        >
          🚪 Hop Out
        </Button>
      )}

      {/* Drive car prompt */}
      {drivePrompt !== null && (
        <div className="absolute inset-0 bg-black/70 flex items-center justify-center z-20" onClick={(e) => e.target === e.currentTarget && setDrivePrompt(null)}>
          <div className="bg-gray-900 border border-yellow-400/60 rounded-xl p-5 max-w-xs text-center" data-testid="drive-car-modal">
            <div className="text-3xl mb-1">🚗</div>
            <h3 className="text-white font-bold text-lg">Take it for a spin?</h3>
            <p className="text-white/60 text-sm mt-1">
              Cruise the block way faster than walking. Steer with the joystick or WASD keys — tap the car (or PARK) to get out.
            </p>
            <div className="flex gap-2 mt-4 justify-center">
              <Button size="sm" variant="outline" onClick={() => setDrivePrompt(null)} data-testid="drive-cancel-btn">Not now</Button>
              <Button size="sm" className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold" onClick={startDriving} data-testid="drive-car-btn">
                🔑 Drive
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Admin mansion claim modal */}
      {adminClaim && (
        <div className="absolute inset-0 bg-black/70 flex items-center justify-center z-20" onClick={(e) => e.target === e.currentTarget && setAdminClaim(null)}>
          <div className="bg-gray-900 border border-sky-400/60 rounded-xl p-5 max-w-xs text-center" data-testid="admin-claim-modal">
            <Shield className="w-8 h-8 text-sky-400 mx-auto mb-2" />
            <h3 className="text-white font-bold text-lg">Claim Admin Mansion {adminClaim === 20 ? 'I' : 'II'}?</h3>
            <p className="text-white/60 text-sm mt-1">
              A staff perk — three grand floors, free for 7 days. Extend anytime from inside, also free.
            </p>
            <div className="flex gap-2 mt-4 justify-center">
              <Button size="sm" variant="outline" onClick={() => setAdminClaim(null)} data-testid="admin-claim-cancel-btn">Not yet</Button>
              <Button size="sm" className="bg-sky-500 hover:bg-sky-400 text-black font-bold" onClick={handleAdminClaim} data-testid="admin-claim-btn">
                <Shield className="w-3 h-3 mr-1" /> Claim Free
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* hidden video fed by the live host's camera for the amphitheater screen */}
      <video ref={screenVideoRef} muted playsInline autoPlay style={{ display: 'none' }} />

      {/* Downtown shop menus */}
      {shopMenu && SHOPS[shopMenu] && (
        <div className="absolute inset-0 bg-black/60 flex items-center justify-center z-20" onClick={(e) => e.target === e.currentTarget && setShopMenu(null)}>
          <div className="bg-gray-900 border rounded-xl p-4 w-72 text-center" style={{ borderColor: `${SHOPS[shopMenu].accent}80` }} data-testid="shop-menu-modal">
            <h3 className="font-bold text-lg" style={{ color: SHOPS[shopMenu].accent }}>{SHOPS[shopMenu].name}</h3>
            <p className="text-white/50 text-xs mb-3 italic">"{SHOPS[shopMenu].line}"</p>
            <div className="space-y-2">
              {SHOPS[shopMenu].menu.map((item) => (
                <button
                  key={item.id}
                  className="w-full flex items-center justify-between bg-black/40 hover:bg-white/10 border border-white/10 rounded-lg px-3 py-2 text-sm text-white transition-colors"
                  onClick={() => handleShopOrder(item)}
                  data-testid={`shop-order-${item.id}`}
                >
                  <span>{item.icon} {item.name}</span>
                  <span className="text-yellow-400 flex items-center gap-1"><Coins className="w-3 h-3" /> {item.price}</span>
                </button>
              ))}
            </div>
            <Button size="sm" variant="outline" className="mt-3" onClick={() => setShopMenu(null)} data-testid="shop-menu-close-btn">
              Just looking, thanks
            </Button>
          </div>
        </div>
      )}

      {/* Turtle Shack menu */}
      {showMenu && (
        <div className="absolute inset-0 bg-black/60 flex items-center justify-center z-20" onClick={(e) => e.target === e.currentTarget && setShowMenu(false)}>
          <div className="bg-gray-900 border border-emerald-400/50 rounded-xl p-4 w-72 text-center" data-testid="diner-menu-modal">
            <h3 className="text-emerald-300 font-bold text-lg">🐢 Turtle Shack</h3>
            <p className="text-white/50 text-xs mb-3 italic">"What can I get started for you today?"</p>
            <p className="text-emerald-200/70 text-[10px] font-bold uppercase tracking-widest text-left mb-1">Drinks</p>
            <div className="space-y-2">
              {DINER_MENU.filter((m) => m.kind === 'drink').map((item) => (
                <button
                  key={item.id}
                  className="w-full flex items-center justify-between bg-black/40 hover:bg-emerald-500/20 border border-white/10 rounded-lg px-3 py-2 text-sm text-white transition-colors"
                  onClick={() => handleOrder(item)}
                  data-testid={`order-${item.id}`}
                >
                  <span>{item.icon} {item.name}</span>
                  <span className="text-yellow-400 flex items-center gap-1"><Coins className="w-3 h-3" /> {item.price}</span>
                </button>
              ))}
            </div>
            <p className="text-emerald-200/70 text-[10px] font-bold uppercase tracking-widest text-left mt-3 mb-1">Food</p>
            <div className="space-y-2">
              {DINER_MENU.filter((m) => m.kind === 'food').map((item) => (
                <button
                  key={item.id}
                  className="w-full flex items-center justify-between bg-black/40 hover:bg-orange-500/20 border border-white/10 rounded-lg px-3 py-2 text-sm text-white transition-colors"
                  onClick={() => handleOrder(item)}
                  data-testid={`order-${item.id}`}
                >
                  <span>{item.icon} {item.name}</span>
                  <span className="text-yellow-400 flex items-center gap-1"><Coins className="w-3 h-3" /> {item.price}</span>
                </button>
              ))}
            </div>
            <Button size="sm" variant="outline" className="mt-3" onClick={() => setShowMenu(false)} data-testid="diner-menu-close-btn">
              Just looking, thanks
            </Button>
          </div>
        </div>
      )}

      {/* Photo remove modal */}
      {photoRemoveIdx !== null && (
        <div className="absolute inset-0 bg-black/70 flex items-center justify-center z-20" onClick={(e) => e.target === e.currentTarget && setPhotoRemoveIdx(null)}>
          <div className="bg-gray-900 border border-pink-400/50 rounded-xl p-5 max-w-xs text-center" data-testid="photo-remove-modal">
            <Camera className="w-8 h-8 text-pink-300 mx-auto mb-2" />
            <h3 className="text-white font-bold">Remove this photo?</h3>
            <div className="flex gap-2 mt-4 justify-center">
              <Button size="sm" variant="outline" onClick={() => setPhotoRemoveIdx(null)} data-testid="photo-remove-cancel-btn">Keep it</Button>
              <Button size="sm" className="bg-pink-600 hover:bg-pink-500 text-white" onClick={() => handleRemovePhoto(photoRemoveIdx)} data-testid="photo-remove-confirm-btn">
                Remove
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Furniture shop */}
      {showShop && (
        <div className="absolute inset-0 bg-black/80 z-30 overflow-y-auto p-3" data-testid="house-shop-modal">
          <div className="flex items-center justify-between mb-2 sticky top-0 bg-black/80 py-1">
            <h3 className="text-white font-bold flex items-center gap-2"><Sofa className="w-4 h-4 text-yellow-400" /> Home Furniture Shop</h3>
            <div className="flex items-center gap-2">
              <span className="text-yellow-400 text-xs flex items-center gap-1"><Coins className="w-3 h-3" /> {vibecoins}</span>
              <Button size="sm" variant="ghost" className="text-white h-7 w-7 p-0" onClick={() => setShowShop(false)} data-testid="house-shop-close-btn">
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <p className="text-white/40 text-[11px] mb-2">Home furniture is separate from your apartment collection.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {FURNITURE.map((item) => {
              const owned = ownedItems.includes(item.id);
              const meta = RARITY_META[item.rarity] || {};
              return (
                <div key={item.id} className="bg-gray-900 border border-white/10 rounded-lg p-2 flex flex-col items-center gap-1" data-testid={`hshop-${item.id}`}>
                  <FurniturePreview id={item.id} />
                  <span className="text-white text-xs font-medium text-center">{item.name}</span>
                  <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold uppercase" style={{ background: meta.bg || '#333', color: meta.color || '#fff' }}>
                    {item.rarity}
                  </span>
                  {owned ? (
                    <span className="text-emerald-400 text-xs font-bold">Owned ✓</span>
                  ) : (
                    <Button size="sm" className="h-6 text-[11px] bg-yellow-500/90 hover:bg-yellow-400 text-black font-bold px-2" onClick={() => handleBuy(item.id)} data-testid={`hbuy-${item.id}`}>
                      <Coins className="w-3 h-3 mr-1" /> {item.price}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

const FurniturePreview = ({ id }) => {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let raf;
    let phase = 0;
    const loop = () => {
      phase += 1;
      ctx.clearRect(0, 0, 90, 80);
      drawFurniture(ctx, id, 45, 62, phase, false);
      raf = requestAnimationFrame(loop);
    };
    loop();
    return () => cancelAnimationFrame(raf);
  }, [id]);
  return <canvas ref={ref} width={90} height={80} className="w-16 h-14" style={{ imageRendering: 'pixelated' }} />;
};
